import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import './App.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import { Routes, Route, Link } from 'react-router-dom';
import TermsOfService from './TermsOfService';
import PrivacyPolicy from './PrivacyPolicy';
import { CoupangAdTop, CoupangAdBottom, CoupangAdSidebar } from './CoupangAd';
import RemoteViewing from './RemoteViewing';
import { Bar, Pie, Scatter } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement,
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement
);

ChartJS.defaults.scales.category.ticks.color = (ctx) => {
    const lbl = ctx.tick.label ?? ctx.scale.getLabelForValue(ctx.tick.value);
    const n = Number(lbl);
    // If the label is not a finite number, use the default color
    if (!Number.isFinite(n)) {
        return '#ccc'; // Fallback to a default color
    }
    return getLottoColorCode(n);
};

const API_URL = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:8000`;

const STAT_OPTIONS = [
    { id: 'appearances_by_number', name: '번호별 출현 횟수' },
    { id: 'hot_cold', name: '핫 | 콜드' },
    { id: 'non_appearance', name: '장기 미출현 번호' },
    { id: 'odd_even_ratio', name: '홀짝 패턴' },
    { id: 'high_low_ratio', name: '고저 패턴' },
    { id: 'total_top5', name: '총합수' },
    { id: 'ac_value', name: 'AC값 분포도' },
    { id: 'consecutive_numbers', name: '연속 번호 쌍' },
    { id: 'isomorphic_numbers', name: '거울수 | 동형수' },
    { id: 'ending_digits', name: '끝수별 출현 횟수' },
    { id: 'elemental_balance', name: '원소(번호) 대역' },
    { id: 'elemental_tree', name: '트리 비율' },
    { id: 'compatibility_numbers', name: '궁합수' },
    { id: 'arcana', name: '메이저 아르카나' },
    { id: 'regression_trends', name: '회귀 분석' },
];

const STAT_DESCRIPTIONS = {
    'appearances_by_number': '1~45번 각 번호가 선택된 기간 동안 몇 번이나 출현했는지 보여줍니다.',
    'hot_cold': '선택된 기간 내 가장 자주 나온 번호(🔥 핫)와 가장 적게 나온 번호(❄️ 콜드)를 분류합니다.',
    'non_appearance': '가장 오랫동안 당첨번호로 나오지 않은 상위 15개 번호와 미출현 기간을 표시합니다.',
    'odd_even_ratio': '한 회차의 6개 번호의 홀수/짝수 구성 비율(예: 3:3, 4:2)의 통계입니다.',
    'high_low_ratio': '낮은 번호(1~22)와 높은 번호(23~45)의 구성 비율(예: 3:3, 4:2)의 통계입니다.',
    'total_top5': '6개 번호의 합계가 가장 자주 나온 상위 5개 구간을 보여줍니다.',
    'ac_value': '번호 조합의 산술적 복잡도(AC)를 계산하여 분포를 표시합니다. 높을수록 번호가 골고루 분포됨을 의미합니다.',
    'consecutive_numbers': '1-2, 33-34처럼 연속된 번호 쌍의 출현 분포를 보여줍니다.',
    'isomorphic_numbers': '12-21처럼 자릿수가 대칭인 거울수와, 4-14-24처럼 끝자리가 같은 동끝수의 출현 통계입니다.',
    'ending_digits': '각 번호의 끝자리(0~9)가 몇 회 나왔는지 분석합니다.',
    'elemental_balance': '5개 원소 번호 그룹(공기💨, 물💧, 불🔥, 흙🌿, 아카샤✨)의 출현 빈도를 보여줍니다.',
    'elemental_tree': '전체 경우의 수를 5개 트리로 분류했을 때, 각 트리에서 당첨번호가 나온 비율(%)을 표시합니다.',
    'compatibility_numbers': '기간 내 선택한 번호와 동반해 가장 자주 나온 번호들의 상위 10개를 분석합니다.',
    'arcana': '당첨번호 조합의 수비학적 상징으로 등장한 메이저 타로카드 22장의 출현 빈도 순위입니다.',
    'regression_trends': '시간 경과에 따라 특정 번호의 출현 빈도가 증가(📈 상승 추세)하는지 감소(📉 하락 추세)하는지 분석합니다.'
};

const DEFAULT_STATS = ['hot_cold', 'appearances_by_number'];
const ODD_EVEN_RATIOS = ['6:0', '5:1', '4:2', '3:3', '2:4', '1:5', '0:6'];
const HIGH_LOW_RATIOS = ['6:0', '5:1', '4:2', '3:3', '2:4', '1:5', '0:6'];
const TREE_MAP = {
    'AB': '1번 트리',
    'CD': '2번 트리',
    'EF': '3번 트리',
    'GH': '4번 트리',
    'IJ': '5번 트리'
};

const GOAL_ACHIEVEMENT_SPREAD = [
    { title: '1. 현재 상황', description: '당신의 현재 재물운의 기운' },
    { title: '2. 도움이 되는 것', description: '행운을 끌어당기거나 돕는 긍정적 요소' },
    { title: '3. 방해되는 것', description: '행운을 가로막는 부정적 요소' },
    { title: '4. 조언', description: '행운을 위해 당신이 취해야 할 행동' },
    { title: '5. 가까운 미래', description: '가까운 미래에 다가올 금전적 흐름' },
    { title: '6. 최종 결과', description: '이번 주 로또에 대한 당신의 잠재적 가능성' }
];

const CELTIC_CROSS_SPREAD = [
    { title: '1. 현재', description: '당신의 현재 상황과 질문의 핵심' },
    { title: '2. 장애물', description: '행운을 향한 길의 장애물 또는 과제' },
    { title: '3. 과거의 영향', description: '과거의 선택이 현재에 미치는 영향' },
    { title: '4. 최근 과거', description: '최근에 일어난 행운의 변화' },
    { title: '5. 기대할 수 있는 것', description: '당신이 기대할 수 있는 최선의 결과' },
    { title: '6. 가까운 미래', description: '곧바로 당신 앞에 펼쳐질 상황' }
];

const initialFiltersState = {
    fixedNumbers: [],
    excludedNumbers: [],
    oddEvenRatio: [],
    highLowRatio: [],
    sumRangeMin: 81,
    sumRangeMax: 180,
    acValueMin: 0,
    acValueMax: 10,
    excludeConsecutive: false,
    excludeMirrors: false,
    excludeSameEnding3: false,
    excludeConsecutive3: false,
    excludeBlacklist: true,
    elementalBalance: { air: 0, water: 0, fire: 0, earth: 0, spirit: 0 },
    includeTrees: [],
    excludeTrees: [],
    trendingTreePeriod: 10,
    trendingTreeCount: 5,
    excludeSameRange: 0,
    includeMajorArcana: [],
    excludePastWinners: false,
    num_combinations: 1,
};


// --- Helper Functions ---
const getCardImageName = (number) => {
    if (number === 41) return 'Swords14.png';
    if (number === 42) return 'Cups13.png';
    if (number === 43) return 'Wands12.png';
    if (number === 44) return 'Pentacles11.png';
    if (number === 45) return '21-TheWorld.png';
    const suitMap = { 0: 'Swords', 1: 'Cups', 2: 'Wands', 3: 'Pentacles' };
    const suitIndex = Math.floor((number - 1) / 10);
    const numberInSuit = (number - 1) % 10 + 1;
    const suit = suitMap[suitIndex];
    const numStr = numberInSuit === 1 ? '01' : String(numberInSuit).padStart(2, '0');
    return `${suit}${numStr}.png`;
};

const getMajorCardImageName = (number, name) => {
    const numStr = String(number).padStart(2, '0');
    const nameStr = name.replace(/ /g, '').replace(/\./g, '');
    return `${numStr}-${nameStr}.png`;
};

const getLottoColorCode = (number) => {
    if (number >= 1 && number <= 10) return '#fbc400';
    if (number >= 11 && number <= 20) return '#69c8f2';
    if (number >= 21 && number <= 30) return '#ff7272';
    if (number >= 31 && number <= 40) return '#aaa';
    if (number >= 41 && number <= 45) return '#b0d840';
    return '#e0e0e0'; // Default color
};

const getLottoColorClass = (number) => {
    if (number >= 1 && number <= 10) return 'lotto-color-1';
    if (number >= 11 && number <= 20) return 'lotto-color-2';
    if (number >= 21 && number <= 30) return 'lotto-color-3';
    if (number >= 31 && number <= 40) return 'lotto-color-4';
    if (number >= 41 && number <= 45) return 'lotto-color-5';
    return '';
};

const getCardMeaningFileName = (number) => {
    if (number === 41) return 'King_of_Swords.txt';
    if (number === 42) return 'Queen_of_Cups.txt';
    if (number === 43) return 'Knight_of_Wands.txt';
    if (number === 44) return 'Page_of_Pentacles.txt';
    if (number === 45) return 'The_Universe.txt';
    const suitMap = { 0: 'Swords', 1: 'Cups', 2: 'Wands', 3: 'Pentacles' };
    const suitIndex = Math.floor((number - 1) / 10);
    const numberInSuit = (number - 1) % 10 + 1;
    const suit = suitMap[suitIndex];
    if (numberInSuit === 1) return `Ace_of_${suit}.txt`;
    return `${numberInSuit}_of_${suit}.txt`;
};

const shuffleArray = (array) => {
    let currentIndex = array.length, randomIndex;
    while (currentIndex !== 0) {
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex--;
        [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
    }
    return array;
};

const initialDeck = Array.from({ length: 45 }, (_, i) => i + 1);

const coupangDynamicBannerSources = [
    // 1. 쿠팡 추천 (2개)
    ...Array(2).fill("https://ads-partners.coupang.com/widgets.html?id=949448&template=carousel&trackingCode=AF5327763&subId=&width=680&height=140&tsource="),
    // 2. 고객 관심기반 (5개)
    ...Array(5).fill("https://ads-partners.coupang.com/widgets.html?id=949476&template=carousel&trackingCode=AF5327763&subId=&width=100%&height=140&tsource="),
    // 3. 카테고리 베스트 (각 1개씩, 총 11개)
    "https://ads-partners.coupang.com/widgets.html?id=949477&template=carousel&trackingCode=AF5327763&subId=&width=100%&height=140&tsource=", // 뷰티
    "https://ads-partners.coupang.com/widgets.html?id=949478&template=carousel&trackingCode=AF5327763&subId=&width=100%&height=140&tsource=", // 식품
    "https://ads-partners.coupang.com/widgets.html?id=949479&template=carousel&trackingCode=AF5327763&subId=&width=100%&height=140&tsource=", // 생활용품
    "https://ads-partners.coupang.com/widgets.html?id=949484&template=carousel&trackingCode=AF5327763&subId=&width=100%&height=140&tsource=", // 패션
    "https://ads-partners.coupang.com/widgets.html?id=949483&template=carousel&trackingCode=AF5327763&subId=&width=100%&height=140&tsource=", // 쿠팡 수입
    "https://ads-partners.coupang.com/widgets.html?id=949482&template=carousel&trackingCode=AF5327763&subId=&width=100%&height=140&tsource=", // 자동차
    "https://ads-partners.coupang.com/widgets.html?id=949481&template=carousel&trackingCode=AF5327763&subId=&width=100%&height=140&tsource=", // 가전
    "https://ads-partners.coupang.com/widgets.html?id=949480&template=carousel&trackingCode=AF5327763&subId=&width=100%&height=140&tsource=", // 홈인테리어
    "https://ads-partners.coupang.com/widgets.html?id=949485&template=carousel&trackingCode=AF5327763&subId=&width=100%&height=140&tsource=", // 스포츠/레저
    "https://ads-partners.coupang.com/widgets.html?id=949486&template=carousel&trackingCode=AF5327763&subId=&width=100%&height=140&tsource=", // 출산/유아동
    "https://ads-partners.coupang.com/widgets.html?id=949489&template=carousel&trackingCode=AF5327763&subId=&width=100%&height=140&tsource=", // 헬스/건강식품
];

const RandomDynamicBanner = () => {
    const [bannerUrl, setBannerUrl] = useState('');
    const [key, setKey] = useState(0); // iframe 리로드를 위한 키

    useEffect(() => {
        // 초기 배너 설정
        changeBanner();

        // 60초(1분)마다 배너 교체
        const interval = setInterval(() => {
            changeBanner();
        }, 60000); // 60초

        return () => clearInterval(interval);
    }, []);

    const changeBanner = () => {
        setBannerUrl(prevUrl => {
            let newUrl;
            let attempts = 0;
            // 이전과 다른 배너가 나올 때까지 반복 (최대 5번 시도)
            do {
                const randomIndex = Math.floor(Math.random() * coupangDynamicBannerSources.length);
                newUrl = coupangDynamicBannerSources[randomIndex];
                attempts++;
            } while (newUrl === prevUrl && attempts < 5);
            
            setKey(prev => prev + 1); // iframe 강제 리렌더링
            return newUrl;
        });
    };

    if (!bannerUrl) return null;

    return (
        <div className="ad-placeholder-banner" style={{ margin: '2rem auto 1rem auto', backgroundColor: 'rgba(0,0,0,0.02)' }}>
            <iframe
                key={key} // Key가 바뀌면 iframe이 새로고침됨
                title="쿠팡 파트너스 추천 광고"
                src={bannerUrl}
                width="100%"
                height="140"
                frameBorder="0"
                scrolling="no"
                referrerPolicy="unsafe-url"
                browsingtopics="true"
                style={{ display: 'block', border: 'none' }}
            ></iframe>
        </div>
    );
};

const HistoryCard = ({ drawInfo, majorMeanings }) => {
    const { drwNo, drwNoDate, numbers, bonus_number, total_sum, odd_even_ratio, ac_value, elemental_balance, major_arcana_card } = drawInfo;

    // Get tarot card name from majorMeanings
    const tarotName = majorMeanings && majorMeanings[major_arcana_card] ? majorMeanings[major_arcana_card].korean_name : '';

    return (
        <div className="history-card">
            <div className="history-info-left">
                <span className="history-drw-no">{drwNo}회</span>
                <span className="history-date">{drwNoDate}</span>
            </div>
            <div className="history-numbers">
                {numbers.map(n => (
                    <span key={n} className={`premium-number-cell ${getLottoColorClass(n)}`}>{n}</span>
                ))}
                <span className="bonus-plus">+</span>
                <span className={`number-cell bonus ${getLottoColorClass(bonus_number)}`}>{bonus_number}</span>
            </div>
            <div className="history-stats-right">
                <div className="stat-badge" title="총합">
                    <span className="badge-label">합계</span>
                    <span className="badge-value">{total_sum}</span>
                </div>
                <div className="stat-badge" title="홀:짝">
                    <span className="badge-label">홀짝</span>
                    <span className="badge-value">{odd_even_ratio}</span>
                </div>
                <div className="stat-badge" title="고:저">
                    <span className="badge-label">고저</span>
                    <span className="badge-value">{drawInfo.high_low_ratio}</span>
                </div>
                <div className="stat-badge" title="AC값">
                    <span className="badge-label">AC</span>
                    <span className="badge-value">{ac_value}</span>
                </div>
                <div className="stat-badge elemental-badge" title={`원소: 💨${elemental_balance.air} 💧${elemental_balance.water} 🔥${elemental_balance.fire} 🌿${elemental_balance.earth} ✨${elemental_balance.spirit}`}>
                    <span className="badge-label">원소</span>
                    <span className="badge-value">
                        {elemental_balance.air > 2 ? '💨' : ''}
                        {elemental_balance.water > 2 ? '💧' : ''}
                        {elemental_balance.fire > 2 ? '🔥' : ''}
                        {elemental_balance.earth > 2 ? '🌿' : ''}
                        {elemental_balance.spirit > 2 ? '✨' : ''}
                        {!Object.values(elemental_balance).some(v => v > 2) && '⚖️'}
                    </span>
                </div>
                <div className="stat-badge tarot-badge" title={`메이저 아르카나: ${tarotName}`}>
                    <span className="badge-label">타로</span>
                    <span className="badge-value">{tarotName}</span>
                </div>
            </div>
        </div>
    );
};

const WinningHistoryList = ({ historyData, currentPage, totalPages, onPageChange, majorMeanings }) => {
    if (!historyData || historyData.length === 0) {
        return <div className="stats-content empty"><p>조회된 당첨 내역이 없습니다.</p></div>;
    }

    const startIndex = (currentPage - 1) * 15;
    const currentData = historyData.slice(startIndex, startIndex + 15);

    return (
        <div className="history-list-container fade-in">
            {currentData.map(info => (
                <HistoryCard key={info.drwNo} drawInfo={info} majorMeanings={majorMeanings} />
            ))}

            {totalPages > 1 && (
                <div className="pagination-container neon-pagination">
                    <button
                        className="pagination-btn prev"
                        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                    >
                        &lt;
                    </button>

                    <div className="pagination-pages">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            // Simple logic to show window of pages around current
                            let pageNum = currentPage;
                            if (totalPages <= 5) pageNum = i + 1;
                            else if (currentPage <= 3) pageNum = i + 1;
                            else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                            else pageNum = currentPage - 2 + i;

                            return (
                                <button
                                    key={pageNum}
                                    className={`pagination-page ${currentPage === pageNum ? 'active' : ''}`}
                                    onClick={() => onPageChange(pageNum)}
                                >
                                    {pageNum}
                                </button>
                            );
                        })}
                    </div>

                    <button
                        className="pagination-btn next"
                        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                    >
                        &gt;
                    </button>
                </div>
            )}
        </div>
    );
};

const AppHeader = ({ activeTab, handleTabChange, handleReset, onLoginClick }) => {
    return (
        <header className="app-header">
            <div className="header-title">
                <h1 onClick={handleReset}>LOTTO & TAROT</h1>
            </div>
            <nav className="tab-nav">
                <button className={`tab-button hover-levitate ${activeTab === 'tarot' ? 'active' : ''}`} onClick={(e) => handleTabChange('tarot', e)}>로또 타로방</button>
                <button className={`tab-button hover-levitate ${activeTab === 'analysis' ? 'active' : ''}`} onClick={(e) => handleTabChange('analysis', e)}>통계 분석실</button>
                {/* 'stats' tab refers to '조합 생성실 (Combination Generation Room)' */}
                <button className={`tab-button hover-levitate ${activeTab === 'stats' ? 'active' : ''}`} onClick={(e) => handleTabChange('stats', e)}>조합 생성실</button>
                {/* <button className={`tab-button hover-levitate ${activeTab === 'gallery' ? 'active' : ''}`} onClick={(e) => handleTabChange('gallery', e)}>갤러리(가칭)</button> */}
            </nav>
            <div className="header-actions">
                {/* <button className="login-button" onClick={onLoginClick}>로그인</button> */}
            </div>
        </header>
    );
};

const LatestDrawInfo = ({ drawInfo, majorMeanings, onMajorCardEnter, onMajorCardLeave, onPrev, onNext, onDateClick, isDropdownOpen, allDrawNumbers, onDrawSelect, dropdownRef }) => {
    if (!drawInfo) {
        return <div className="latest-draw-info-placeholder"><h4>최신 당첨 정보를 불러오는 중...</h4></div>;
    }

    const { numbers, bonus_number, drwNo, drwNoDate, win_history, total_sum, odd_even_ratio, high_low_ratio, elemental_balance, major_arcana_card, ac_value } = drawInfo;

    const majorCardData = majorMeanings ? majorMeanings[major_arcana_card] : null;

    return (
        <div className="latest-draw-info">
            <div className="latest-draw-main-content">
                <div className="latest-draw-header">
                    <button onClick={onPrev} className="nav-arrow">{'<'}</button>
                    <h4>{drwNo}회 로또 당첨번호</h4>
                    <div className="date-container" ref={dropdownRef}>
                        <span onClick={onDateClick} className="date-display">({drwNoDate}) ▼</span>
                        {isDropdownOpen &&
                            <Drawdown
                                options={allDrawNumbers}
                                onSelect={onDrawSelect}
                            />
                        }
                    </div>
                    <button onClick={onNext} className="nav-arrow">{'>'}</button>
                </div>
                <div className="number-display">
                    <div className="win-numbers">
                        {numbers.map(n => <span key={n} className={`number-cell ${getLottoColorClass(n)}`}>{n}</span>)}
                    </div>
                    <div className="bonus-number">
                        <span>+</span>
                        <span className={`number-cell bonus ${getLottoColorClass(bonus_number)}`}>{bonus_number}</span>
                    </div>
                </div>
                <p className="details">
                    {win_history && <span><strong>역대 당첨:</strong> {win_history} |</span>}
                    <span><strong>합계:</strong> {total_sum} |</span>
                    <span><strong>홀짝:</strong> {odd_even_ratio} |</span>
                    <span><strong>고저:</strong> {high_low_ratio} |</span>
                    {ac_value !== undefined && <span> | <strong>AC값:</strong> {ac_value}</span>}
                    <span><strong>원소:</strong> 💨{elemental_balance.air} 💧{elemental_balance.water} 🔥{elemental_balance.fire} 🌿{elemental_balance.earth} ✨{elemental_balance.spirit}</span>
                </p>
            </div>
            {majorCardData &&
                <div className="latest-draw-card">
                    <img
                        src={`${process.env.PUBLIC_URL}/Cards-png/${getMajorCardImageName(major_arcana_card, majorCardData.name)}`}
                        alt={majorCardData.name}
                        className="major-arcana-icon"
                        onMouseEnter={(e) => onMajorCardEnter(major_arcana_card, e)}
                        onMouseLeave={onMajorCardLeave}
                    />
                </div>
            }
        </div>
    );
};

/*
const InterstitialAdPlaceholder = ({ onClose, title }) => {
    return (
        <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '80vw', height: '80vh', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <div style={{ flexGrow: 1, backgroundColor: '#222', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '2rem' }}>
                    {title || '전면 광고 (자리표시자)'}
                </div>
                <button onClick={onClose} style={{ padding: '1rem', fontSize: '1.2rem', cursor: 'pointer' }}>광고 닫고 계속하기</button>
            </div>
        </div>
    );
};
*/

// --- Main App Component ---
function App() {
    const [activeTab, setActiveTab] = useState('tarot');
    const [currentPage, setCurrentPage] = useState('main');
    const [results, setResults] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [adRotationKey, setAdRotationKey] = useState(0);
    const [selectedCards, setSelectedCards] = useState([]);
    const [cards, setCards] = useState(() => shuffleArray([...initialDeck]));
    const [isShuffling, setIsShuffling] = useState(false);
    const [tooltip, setTooltip] = useState({ visible: false, content: '', x: 0, y: 0 });
    const [analysisData, setAnalysisData] = useState({});
    const [majorMeanings, setMajorMeanings] = useState(null);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const resultsContainerRef = useRef(null);
    const tarotBoardRef = useRef(null);
    const drawDropdownRef = useRef(null);

    // Handle URL query parameters for direct linking
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const tab = params.get('tab');
        const mode = params.get('mode');

        if (tab === 'rv') {
            setActiveTab('tarot');
            setSelectedSpread('rv');
        } else if (tab === 'celtic') {
            setActiveTab('tarot');
            setSelectedSpread('celticCross');
        } else if (tab === 'analysis') {
            setActiveTab('analysis');
            if (mode === 'history') {
                setAnalysisMode('history');
            } else {
                setAnalysisMode('chart');
            }
        } else if (tab === 'stats' || tab === 'generator' || tab === 'filter') {
            setActiveTab('stats');
        }
    }, []);

    const coupangAdData = {
        partnerId: 'AF5327763',
        // 상단 핵심 프로모션 배너 (오늘의 특가)
        topBanner: {
            title: '🔥 쿠팡 오늘의 특가를 확인하세요!',
            url: 'https://link.coupang.com/a/daXvLa',
            cta: '특가 보러가기 →'
        },
        // 하단 3열 그리드용 상품들
        bottomProducts: [
            {
                name: '이태리산 정품 유니버셜 웨이트 타로 카드',
                url: 'https://link.coupang.com/a/dasZsw',
                imageUrl: 'https://thumbnail.coupangcdn.com/thumbnails/remote/492x492ex/image/vendor_inventory/797e/ac2f77e6b06c2e4c958bf4d90b3b4514421bb88b34a405be989663c56b17.jpg',
                // price: 21000
            },
            {
                name: '골드팡 24k 행운의 골든클로버 순금코인 0.2g',
                url: 'https://link.coupang.com/a/dbeNmi',
                imageUrl: 'https://thumbnail.coupangcdn.com/thumbnails/remote/320x320ex/image/vendor_inventory/3bc3/2eacbd18ad60758a96ccae5c25e45667cb3bb6f40bf03447e8c055edf085.jpg',
                // price: 68900
            },
            {
                name: '돈 들어오는 황금 돈나무 그림 풍수 액자',
                url: 'https://link.coupang.com/a/dbl5B9',
                imageUrl: 'https://thumbnail.coupangcdn.com/thumbnails/remote/320x320ex/image/vendor_inventory/f3e7/f02c5e7436d9e40ac066977d5aa6b2c5a2c239e3e66254af7614dbec94ea.jpg',
                // price: 12900
            },
            {
                name: '돈 들어오는 황금 돌고래 대형 캔버스 풍수 액자',
                url: 'https://link.coupang.com/a/dbcrmo',
                imageUrl: 'https://thumbnail.coupangcdn.com/thumbnails/remote/320x320ex/image/vendor_inventory/ffee/be9cfdf99e7bac6f2e07a3d0d6f9bd91a525fb784f2944bbf9d00e7f59e1.jpg',
                // price: 12900
            },
            {
                name: '금전운 재물운 만사형통 코뚜레 풍경종',
                url: 'https://link.coupang.com/a/dbdqoP',
                imageUrl: 'https://thumbnail.coupangcdn.com/thumbnails/remote/320x320ex/image/vendor_inventory/92a4/8577dd0dbabeccad2e499dad4581cc2a2336b85f17f31a49d89ec4510a35.jpg',
                // price: 9300
            },
            {
                name: '그린테라피 미니 푸딩 개운죽 행운목',
                url: 'https://link.coupang.com/a/dbb2cb',
                imageUrl: 'https://thumbnail.coupangcdn.com/thumbnails/remote/320x320ex/image/vendor_inventory/f2a2/e20e584f45556438bb3867bf5c0d8e4b37761e7528e89784e5d25dd8b990.jpg',
                // price: 12900

            }

        ],
        // 좌측 사이드바용 배너 (3개)
        leftSidebarProducts: [
            {
                name: '골드박스',
                url: 'https://link.coupang.com/a/dbiEqy',
                imageUrl: 'https://image11.coupangcdn.com/image/affiliate/event/promotion/2025/04/02/8b9fe570d2770053012c3c186f0a4205.png'
            },
            {
                name: '이달의 신상',
                url: 'https://link.coupang.com/a/dbiLI5',
                imageUrl: 'https://img4c.coupangcdn.com/image/affiliate/event/promotion/2025/03/17/7810d0c625da002001f6b35917b6dd21.png'
            },
            {
                name: '가전디지털, 게이밍 노트북',
                url: 'https://link.coupang.com/a/dbirlc',
                imageUrl: 'https://image3.coupangcdn.com/image/affiliate/event/promotion/2024/08/16/7cd22fb2b43900fa0115e7eeed2060c4.png'
            }
        ],
        // 우측 사이드바용 배너 (3개)
        rightSidebarProducts: [
            {
                name: '가전디지털, AI가전 모음전',
                url: 'https://link.coupang.com/a/dbi0Mp',
                imageUrl: 'https://image2.coupangcdn.com/image/affiliate/event/promotion/2025/03/17/6b10aac631da006a01f61f5917b59714.png'
            },
            {
                name: '홈인테리어, 로켓설치 가구/리빙 특가 상품',
                url: 'https://link.coupang.com/a/dbjdbx',
                imageUrl: 'https://image10.coupangcdn.com/image/affiliate/event/promotion/2024/09/27/1578b25ca54100dc01fcbc101842b57f.png'
            },
            {
                name: '로켓프레시, 1일 1특가',
                url: 'https://link.coupang.com/a/dbjlrO',
                imageUrl: 'https://image2.coupangcdn.com/image/affiliate/event/promotion/2025/11/14/721ef6151fb800ea01d66ed0fbb6e6ed.png'
            }
        ],
        // 기존 호환성을 위한 products 배열 (deprecated)
        products: [
            {
                name: '코리아보드게임즈 유니버설 웨이트 타로 카드',
                url: 'https://link.coupang.com/a/dasZsw',
                imageUrl: 'https://thumbnail.coupangcdn.com/thumbnails/remote/492x492ex/image/vendor_inventory/797e/ac2f77e6b06c2e4c958bf4d90b3b4514421bb88b34a405be989663c56b17.jpg'
            }
        ]
    };

    // State for Tarot Tab
    const [selectedSpread, setSelectedSpread] = useState('grid');
    const [resultSourceSpread, setResultSourceSpread] = useState(null);
    const [spreadCards, setSpreadCards] = useState([]);
    const [discardPile, setDiscardPile] = useState([]);
    const [deckOffset, setDeckOffset] = useState(0);
    const [isDealing, setIsDealing] = useState(false);

    // State for Draw Navigation
    const [currentDrwNo, setCurrentDrwNo] = useState(null);
    const [isDrawDropdownOpen, setIsDrawDropdownOpen] = useState(false);

    // State for Analysis Tab
    const [minDrwNo, setMinDrwNo] = useState(1);
    const [maxDrwNo, setMaxDrwNo] = useState(1);
    const [startDrwNo, setStartDrwNo] = useState(1);
    const [endDrwNo, setEndDrwNo] = useState(1);
    const [includeBonus, setIncludeBonus] = useState(false);
    const [quickRange, setQuickRange] = useState('13'); // State for the quick range dropdown
    const [latestDrawInfo, setLatestDrawInfo] = useState(null);
    const [updateStatus, setUpdateStatus] = useState({ loading: false, message: '', error: false });
    const [selectedStats, setSelectedStats] = useState(DEFAULT_STATS);
    const [compatibilityQueryTrigger, setCompatibilityQueryTrigger] = useState(0);
    const [regressionDetail, setRegressionDetail] = useState(null);
    const [regressionDetailLoading, setRegressionDetailLoading] = useState(false);
    const [regressionTarget, setRegressionTarget] = useState(null);
    const [significantOnly, setSignificantOnly] = useState(false);
    const [winningHistory, setWinningHistory] = useState([]);
    const [historyPage, setHistoryPage] = useState(1);
    const [analysisMode, setAnalysisMode] = useState('chart'); // 'chart' or 'history'
    const [hasInitialized, setHasInitialized] = useState(false); // Track if analysis tab has been initialized

    // State for Filter Tab
    const [filters, setFilters] = useState(initialFiltersState);

    // const [showInterstitial, setShowInterstitial] = useState(false);
    // const [showTabInterstitial, setShowTabInterstitial] = useState(false);
    // const [nextTab, setNextTab] = useState(null);
    const [showScrollToTop, setShowScrollToTop] = useState(false);

    const fetchAnalysisData = useCallback(async (start, end, bonus, types) => {
        setLoading(true);
        setError('');
        const params = { start_drw_no: start, end_drw_no: end, include_bonus: bonus };

        const dataPromises = {};

        if (types.includes('hot_cold')) {
            dataPromises.hotCold = axios.get(`${API_URL}/api/analysis/hot-cold`, { params });
        }
        if (types.includes('total_top5')) {
            dataPromises.sumRange = axios.get(`${API_URL}/api/analysis/sum-range`, { params });
        }
        if (types.includes('elemental_tree')) {
            dataPromises.treeRatios = axios.get(`${API_URL}/api/analysis/tree-ratios`);
        }
        if (types.includes('arcana')) {
            dataPromises.arcanaDistribution = axios.get(`${API_URL}/api/analysis/arcana-distribution`, { params });
        }
        if (types.includes('odd_even_ratio')) {
            dataPromises.oddEvenRatio = axios.get(`${API_URL}/api/analysis/odd-even-ratio`, { params });
        }
        if (types.includes('high_low_ratio')) {
            dataPromises.highLowRatio = axios.get(`${API_URL}/api/analysis/high-low-ratio`, { params });
        }
        if (types.includes('appearances_by_number')) {
            dataPromises.appearancesByNumber = axios.get(`${API_URL}/api/analysis/appearances-by-number`, { params });
        }
        if (types.includes('elemental_balance')) {
            dataPromises.elementalBalance = axios.get(`${API_URL}/api/analysis/elemental-balance`, { params });
        }
        if (types.includes('consecutive_numbers')) {
            dataPromises.consecutiveNumbers = axios.get(`${API_URL}/api/analysis/consecutive-numbers`, { params });
        }
        if (types.includes('non_appearance')) {
            dataPromises.nonAppearance = axios.get(`${API_URL}/api/analysis/non-appearance`, { params });
        }
        if (types.includes('ending_digits')) {
            dataPromises.endingDigits = axios.get(`${API_URL}/api/analysis/ending-digits`, { params });
        }
        if (types.includes('ac_value')) {
            dataPromises.acValue = axios.get(`${API_URL}/api/analysis/ac-value`, { params });
        }
        if (types.includes('isomorphic_numbers')) {
            dataPromises.isomorphicNumbers = axios.get(`${API_URL}/api/analysis/isomorphic-numbers`, { params });
        }
        if (types.includes('regression_trends')) {
            dataPromises.regressionTrends = axios.get(`${API_URL}/api/analysis/regression-trends`, { params });
        }

        try {
            const responses = await Promise.all(Object.values(dataPromises));
            const keys = Object.keys(dataPromises);

            const newAnalysisData = {};
            responses.forEach((res, index) => {
                const key = keys[index];
                newAnalysisData[key] = res.data;
            });

            setAnalysisData(prevData => ({ ...prevData, ...newAnalysisData }));

        } catch (err) {
            setError('통계 데이터를 불러오는 데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    }, []);

    const handleRegressionNumberClick = useCallback(async (number, forceRefresh = false) => {
        if (regressionTarget === number && !forceRefresh) {
            setRegressionTarget(null); // Toggle off if clicking the same number
            setRegressionDetail(null);
            return;
        }
        setRegressionTarget(number);
        setRegressionDetailLoading(true);
        try {
            const params = { start_drw_no: startDrwNo, end_drw_no: endDrwNo, include_bonus: includeBonus };
            const response = await axios.get(`${API_URL}/api/analysis/regression-detail/${number}`, { params });
            setRegressionDetail(response.data);
        } catch (err) {
            console.error("Failed to load regression detail:", err);
            setError('회귀 분석 상세 데이터를 불러오는 데 실패했습니다.');
        } finally {
            setRegressionDetailLoading(false);
        }
    }, [startDrwNo, endDrwNo, includeBonus, regressionTarget]);

    const handleAnalysisQuery = useCallback(() => {
        fetchAnalysisData(startDrwNo, endDrwNo, includeBonus, selectedStats);
        setCompatibilityQueryTrigger(c => c + 1);
        if (regressionTarget) {
            handleRegressionNumberClick(regressionTarget, true);
        }
        setAnalysisMode('chart');
    }, [startDrwNo, endDrwNo, includeBonus, selectedStats, fetchAnalysisData, regressionTarget, handleRegressionNumberClick]);

    const handleWinningHistoryQuery = useCallback(async () => {
        const numWeeks = endDrwNo - startDrwNo + 1;
        if (numWeeks > 52) {
            alert("당첨번호 조회는 최대 1년(52회차)까지 가능합니다.");
            return;
        }

        setLoading(true);
        setError('');
        try {
            console.log("Fetching winning history for range:", startDrwNo, "~", endDrwNo);
            const params = { start_drw_no: startDrwNo, end_drw_no: endDrwNo };
            const response = await axios.get(`${API_URL}/api/analysis/draw-history`, { params });
            console.log("Fetched history data:", response.data);
            setWinningHistory(response.data);
            setHistoryPage(1);
            setAnalysisMode('history');
        } catch (err) {
            console.error("Failed to fetch winning history:", err);
            setError('당첨 번호 내역을 불러오는 데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    }, [startDrwNo, endDrwNo]);

    const handleStatSelection = (statId) => {
        const newSelection = [...selectedStats];
        if (newSelection.includes(statId)) {
            newSelection.splice(newSelection.indexOf(statId), 1);
        } else {
            newSelection.push(statId);
        }
        setSelectedStats(newSelection);
        fetchAnalysisData(startDrwNo, endDrwNo, includeBonus, newSelection);
    };

    const handleStatsReset = () => {
        setSelectedStats([]);
    };

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const rangeRes = await axios.get(`${API_URL}/api/analysis/draw-range`);
                const { min_draw, max_draw } = rangeRes.data;
                setMinDrwNo(min_draw);
                setMaxDrwNo(max_draw);
                const initialStart = max_draw;
                setStartDrwNo(initialStart);
                setEndDrwNo(max_draw);
                setQuickRange('');
                setCurrentDrwNo(max_draw);

                fetchAnalysisData(initialStart, max_draw, false, selectedStats);

            } catch (err) {
                console.error("Failed to load initial analysis data:", err);
                setError('분석 탭 초기 데이터를 불러오는 데 실패했습니다.');
            }
        };

        if (activeTab === 'analysis' && !hasInitialized) {
            fetchInitialData();
            setHasInitialized(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, fetchAnalysisData, hasInitialized]);

    useEffect(() => {
        if (currentDrwNo === null) return;

        const fetchDrawInfo = async () => {
            try {
                const response = await axios.get(`${API_URL}/api/analysis/draw-info/${currentDrwNo}`);
                setLatestDrawInfo(response.data);
            } catch (err) {
                console.error(`Failed to load draw info for ${currentDrwNo}:`, err);
                setError('특정 회차 정보를 불러오는 데 실패했습니다.');
            }
        };

        fetchDrawInfo();
    }, [currentDrwNo]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (drawDropdownRef.current && !drawDropdownRef.current.contains(event.target)) {
                setIsDrawDropdownOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [drawDropdownRef]);

    // Sync quick range dropdown with manual date changes
    useEffect(() => {
        if (activeTab !== 'analysis') return;

        let matchedRange = '';
        const numWeeks = maxDrwNo - startDrwNo + 1;

        if (endDrwNo === maxDrwNo) {
            if (numWeeks === 4) matchedRange = '4';
            else if (numWeeks === 13) matchedRange = '13';
            else if (numWeeks === 26) matchedRange = '26';
            else if (numWeeks === 52) matchedRange = '52';
        }

        if (startDrwNo === minDrwNo && endDrwNo === maxDrwNo) {
            matchedRange = '0';
        }

        setQuickRange(matchedRange);

    }, [startDrwNo, endDrwNo, minDrwNo, maxDrwNo, activeTab]);

    useEffect(() => {
        const fetchMeanings = async () => {
            try {
                const response = await axios.get(`${process.env.PUBLIC_URL}/major_card_meanings/meanings.json`);
                setMajorMeanings(response.data);
            } catch (err) {
                console.error("Failed to load major arcana meanings:", err);
            }
        };
        fetchMeanings();
    }, []);

    useEffect(() => {
        if (results && resultsContainerRef.current) {
            resultsContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [results]);

    useEffect(() => {
        const handleScroll = () => {
            const shouldShow = window.scrollY > 800 && activeTab === 'analysis' && selectedStats.length >= 4;
            setShowScrollToTop(shouldShow);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [activeTab, selectedStats]);

    const handleApiCall = async (apiCall, showLoading = true) => {
        if (showLoading) setLoading(true);
        setError('');
        setResults(null);
        setResultSourceSpread(selectedSpread); // Store the source of the result
        try {
            const response = await apiCall();
            setResults(response.data);
            setAdRotationKey(prev => prev + 1); // 번호 생성 시 하단 광고 로테이션
        } catch (err) {
            const errorMessage = err.response?.data?.detail || '오류가 발생했습니다. 서버 상태를 확인하세요.';
            setError(typeof errorMessage === 'object' ? JSON.stringify(errorMessage) : errorMessage);
        } finally {
            if (showLoading) setLoading(false);
        }
    };

    const handleManualUpdate = async () => {
        setUpdateStatus({ loading: true, message: '데이터베이스 업데이트를 시작합니다...', error: false });
        try {
            const response = await axios.post(`${API_URL}/api/run-manual-update`);
            setUpdateStatus({ loading: false, message: response.data.message || '업데이트가 완료되었습니다.', error: false });

            handleAnalysisQuery();
            const latestDrawRes = await axios.get(`${API_URL}/api/analysis/latest-draw-info`);
            setLatestDrawInfo(latestDrawRes.data);
            const rangeRes = await axios.get(`${API_URL}/api/analysis/draw-range`);
            setMinDrwNo(rangeRes.data.min_draw);
            setMaxDrwNo(rangeRes.data.max_draw);

        } catch (err) {
            setUpdateStatus({ loading: false, message: err.response?.data?.detail || '업데이트 중 오류가 발생했습니다.', error: true });
        }
    };

    const handleGenerateFromTarot = () => {
        if (selectedCards.length === 0) {
            alert('최소 1개 이상의 카드를 선택해야 합니다.');
            return;
        }
        if (selectedCards.length < 6) {
            if (window.confirm(`카드를 ${selectedCards.length}개만 선택했습니다. 나머지 카드를 자동으로 선택할까요?`)) {
                handleApiCall(() => axios.post(`${API_URL}/api/generate/tarot`, { selected_numbers: selectedCards }));
            }
        } else {
            handleApiCall(() => axios.post(`${API_URL}/api/generate/tarot`, { selected_numbers: selectedCards }));
        }
    };

    const handleGenerateCutting = () => {
        if (selectedCards.length > 0) {
            if (!window.confirm("선택한 카드가 있습니다. 계속 하시겠습니까?")) {
                return;
            }
        }
        handleApiCall(() => axios.post(`${API_URL}/api/generate/cutting`, { shuffled_deck: cards }));
    };

    const handleGenerateFromCelticCross = () => {
        if (spreadCards.length < 6) {
            alert('🔄 를 눌러 카드를 배열하세요.');
            return;
        }
        const numbersToGenerate = spreadCards.slice(0, 6);
        handleApiCall(() => axios.post(`${API_URL}/api/generate/tarot`, { selected_numbers: numbersToGenerate }));
    };

    const handleCardClick = (number) => {
        const newSelection = [...selectedCards];
        if (newSelection.includes(number)) {
            newSelection.splice(newSelection.indexOf(number), 1);
        } else {
            if (newSelection.length < 6) {
                newSelection.push(number);
            } else {
                setError('최대 6개의 카드만 선택할 수 있습니다.');
            }
        }
        setSelectedCards(newSelection);
    };

    const handleAnimatedShuffle = useCallback(() => {
        if (isShuffling) return;
        setIsShuffling(true);
        setResults(null);
        setSelectedCards([]);
        setError('');

        setSpreadCards([]);
        setDiscardPile([]);
        setDeckOffset(0);

        setTimeout(() => {
            setCards(shuffleArray([...initialDeck]));
            setIsShuffling(false);
        }, 800);
    }, [isShuffling]);

    const handleDealCelticCross = () => {
        if (isDealing) return;

        const board = tarotBoardRef.current;
        if (!board) return;

        const oldSpreadCards = [...spreadCards];
        const newSpreadCards = cards.slice(deckOffset, deckOffset + 10);

        if (deckOffset + 10 > cards.length) {
            alert('마지막 5장입니다. 덱을 다시 섞습니다.');
            setCards(shuffleArray([...initialDeck]));
            setDeckOffset(0);
            setSpreadCards([]);
            setDiscardPile([]);
            return;
        }

        setIsDealing(true);

        const sourceDeckEl = board.querySelector('.source-deck-pile');
        if (sourceDeckEl) {
            sourceDeckEl.classList.add('lift-animation');
            setTimeout(() => {
                sourceDeckEl.classList.remove('lift-animation');
            }, 400);
        }

        const overlay = document.createElement('div');
        overlay.className = 'animation-overlay';
        board.appendChild(overlay);

        const boardRect = board.getBoundingClientRect();
        const getElementCoords = (element) => {
            if (!element) return { top: 0, left: 0, width: 0, height: 0, centerX: 0, centerY: 0 };
            const rect = element.getBoundingClientRect();
            const top = rect.top - boardRect.top;
            const left = rect.left - boardRect.left;
            return {
                top,
                left,
                width: rect.width,
                height: rect.height,
                centerX: left + rect.width / 2,
                centerY: top + rect.height / 2,
            };
        };

        const sourceCoords = getElementCoords(board.querySelector('.source-deck-pile .card-stack'));
        const discardCoords = getElementCoords(board.querySelector('.discard-pile .discard-grid'));
        const slotCoords = Array.from(board.querySelectorAll('.celtic-card-slot')).map(getElementCoords);

        const animateCard = (options) => {
            const cardEl = document.createElement('div');
            cardEl.className = 'animation-card';
            const cardWidth = 80;
            const cardHeight = 138;

            cardEl.style.backgroundImage = `url(${options.imgSrc})`;
            cardEl.style.top = `${options.from.centerY - (cardHeight / 2)}px`;
            cardEl.style.left = `${options.from.centerX - (cardWidth / 2)}px`;
            cardEl.style.transform = options.from.rotation || 'rotate(0deg)';
            overlay.appendChild(cardEl);

            requestAnimationFrame(() => {
                cardEl.style.transition = `transform ${options.duration}s, top ${options.duration}s, left ${options.duration}s`;
                cardEl.style.transitionTimingFunction = 'linear';
                cardEl.style.top = `${options.to.centerY - (cardHeight / 2)}px`;
                cardEl.style.left = `${options.to.centerX - (cardWidth / 2)}px`;
                cardEl.style.transform = options.to.rotation || 'rotate(0deg)';
            });

            setTimeout(() => cardEl.remove(), (options.delay || 0) + (options.duration * 1000) + 50);
        };

        let maxAnimationTime = 0;
        const dealDelay = oldSpreadCards.length > 0 ? 150 : 50;
        const dealDuration = 0.8;

        if (oldSpreadCards.length > 0) {
            const discardDuration = 0.7;
            maxAnimationTime = Math.max(maxAnimationTime, (discardDuration * 1000) + 100);
            oldSpreadCards.forEach((card, index) => {
                animateCard({
                    imgSrc: `${process.env.PUBLIC_URL}/Cards-png/${getCardImageName(card)}`,
                    from: { ...slotCoords[index], rotation: index === 1 ? 'rotate(90deg)' : 'rotate(0deg)' },
                    to: {
                        centerY: discardCoords.centerY,
                        centerX: discardCoords.centerX,
                        rotation: `rotate(${Math.random() * 20 - 10}deg)`
                    },
                    duration: discardDuration,
                });
            });
            setTimeout(() => {
                setDiscardPile(prev => [...oldSpreadCards, ...prev]);
            }, discardDuration * 1000);
        }

        newSpreadCards.forEach((card, index) => {
            const currentDelay = dealDelay; // Remove stagger for simultaneous landing
            maxAnimationTime = Math.max(maxAnimationTime, currentDelay + dealDuration * 1000);
            animateCard({
                imgSrc: `${process.env.PUBLIC_URL}/Cards-png/CardBacks.png`,
                from: { ...sourceCoords, rotation: 'rotate(0deg)' },
                to: { ...slotCoords[index], rotation: index === 1 ? 'rotate(90deg)' : 'rotate(0deg)' },
                duration: dealDuration,
                delay: currentDelay,
            });
        });

        setTimeout(() => {
            setSpreadCards(newSpreadCards);
            setDeckOffset(deckOffset + 10);
            overlay.remove();
            setIsDealing(false);
        }, maxAnimationTime + 100);
    };

    const handleReset = () => {
        setCurrentPage('main');
        handleAnimatedShuffle();
        setActiveTab('tarot');
        setSelectedSpread('grid');
        setSpreadCards([]);
        setDiscardPile([]);
        setDeckOffset(0);
    };

    const handleMouseEnter = async (number, event) => {
        const fileName = getCardMeaningFileName(number);
        try {
            const response = await axios.get(`${process.env.PUBLIC_URL}/tarot_meanings/${fileName}`);
            setTooltip({ visible: true, content: response.data, x: event.clientX + 15, y: event.clientY + 15 });
        } catch (err) {
            setTooltip({ visible: true, content: '설명을 불러올 수 없습니다.', x: event.clientX + 15, y: event.clientY + 15 });
        }
    };

    const handleMajorCardMouseEnter = (cardNumber, event) => {
        if (!majorMeanings || !majorMeanings[cardNumber]) return;
        const cardData = majorMeanings[cardNumber];
        const tooltipContent = (
            <div>
                <strong>{cardData.korean_name} ({cardData.name})</strong>
                <p>{cardData.description}</p>
            </div>
        );
        setTooltip({ visible: true, content: tooltipContent, x: event.clientX + 15, y: event.clientY + 15 });
    };

    const handleMouseLeave = () => {
        setTooltip({ ...tooltip, visible: false });
    };

    const handlePrevDraw = () => {
        if (currentDrwNo > minDrwNo) {
            setCurrentDrwNo(currentDrwNo - 1);
        }
    };

    const handleNextDraw = () => {
        if (currentDrwNo < maxDrwNo) {
            setCurrentDrwNo(currentDrwNo + 1);
        }
    };

    const handleDrawSelect = (drawNumber) => {
        setCurrentDrwNo(drawNumber);
        setIsDrawDropdownOpen(false);
    };

    useEffect(() => {
        setError('');
        setResults(null);
    }, [activeTab, selectedStats]);

    const handleGenerateFiltered = () => {
        const totalSelected = (filters.fixedNumbers || []).length;
        if (totalSelected > 6) {
            alert("고정수는 6개까지만 선택할 수 있습니다.");
            return;
        }

        // --- 전면 광고 로직 임시 비활성화 (주석 처리로 보존) ---
        // setShowInterstitial(true); // Show the ad first

        // --- 광고 비활성화 기간 동안 사용할 임시 로직 ---
        executeGeneration();
    };

    const executeGeneration = () => {
        // API 전송 전에 필터 데이터 변환
        const apiFilters = {
            ...filters,
            oddEvenRatio: filters.oddEvenRatio.length > 0 ? filters.oddEvenRatio.join(',') : null,
            highLowRatio: filters.highLowRatio.length > 0 ? filters.highLowRatio.join(',') : null,
            includeMajorArcana: filters.includeMajorArcana.length > 0 ? filters.includeMajorArcana.join(',') : null,
        };

        handleApiCall(() => axios.post(`${API_URL}/api/generate/filtered`, apiFilters));
    };

    const handleFilterReset = () => {
        setFilters(prev => ({
            ...initialFiltersState,
            num_combinations: prev.num_combinations
        }));
    };

    const handleTabChange = (targetTab, event) => {
        // 탭 메뉴 컨테이너 내에서 정확한 중앙 정렬 스크롤 (페이지 이동 없음)
        if (event && event.currentTarget) {
            const target = event.currentTarget;
            const container = target.parentElement;
            
            if (container) {
                const containerRect = container.getBoundingClientRect();
                const targetRect = target.getBoundingClientRect();
                
                // 현재 스크롤 위치 + (타겟의 화면상 상대 위치) - 중앙 보정값
                const scrollLeft = container.scrollLeft + (targetRect.left - containerRect.left) - (containerRect.width / 2) + (targetRect.width / 2);
                
                container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
            }
        }

        if (targetTab === activeTab) {
            if (activeTab === 'analysis') {
                setAnalysisMode('chart');
            }
            return;
        }

        // --- 광고 로직 임시 비활성화 (주석 처리로 보존) ---
        // const AD_COOLDOWN_MINUTES = 10;
        // const lastAdShownTime = localStorage.getItem('lastTabAdShownTime');
        // const now = new Date().getTime();

        // if (!lastAdShownTime || (now - parseInt(lastAdShownTime, 10)) > AD_COOLDOWN_MINUTES * 60 * 1000) {
        //     setNextTab(targetTab);
        //     setShowTabInterstitial(true);
        // } else {
        //     setCurrentPage('main');
        //     setActiveTab(targetTab);
        // }

        // --- 광고 비활성화 기간 동안 사용할 임시 로직 ---
        setCurrentPage('main');
        setActiveTab(targetTab);
        // 탭 변경 시 하단 광고 로테이션
        setAdRotationKey(prev => prev + 1);
    };

    return (
        <div className="App">
            <title>LOTTO & TAROT - 타로와 로또 통계로 찾는 새로운 행운</title>
            <meta name="description" content="로또 & 타로에서 타로 카드의 신비로운 해석과 역대 당첨 데이터의 통계적 분석을 결합하여, 당신만의 특별한 행운의 번호를 찾아보세요." />
            <AppHeader activeTab={activeTab} handleTabChange={handleTabChange} handleReset={handleReset} onLoginClick={() => setIsLoginModalOpen(true)} />

            <div className="app-body-grid">
                {/* 좌측 사이드바 광고 (3개) */}
                <CoupangAdSidebar products={coupangAdData.leftSidebarProducts} position="left" count={3} />

                <div className="app-main-content">
                    {tooltip.visible && <div className="tooltip" style={{ left: tooltip.x, top: tooltip.y }}>{tooltip.content}</div>}

                    {/* 상단 핵심 프로모션 배너 */}
                    <CoupangAdTop adData={coupangAdData.topBanner} />

                    <main className="tab-content">
                        {currentPage === 'main' && (
                            <>
                                {activeTab === 'tarot' && (
                                    <>
                                        <div className={`container ${results ? 'has-results' : ''}`}>
                                            <SpreadSelector selectedSpread={selectedSpread} setSelectedSpread={setSelectedSpread} />

                                            {selectedSpread === 'rv' && (
                                                <RemoteViewing majorMeanings={majorMeanings} />
                                            )}

                                            {selectedSpread === 'grid' && !results &&
                                                <TarotCardSelector cards={cards} selectedCards={selectedCards} onCardClick={handleCardClick} isShuffling={isShuffling} />
                                            }
                                            {selectedSpread === 'celticCross' && !results &&
                                                <div className="tarot-board" ref={tarotBoardRef}>
                                                    <SourceDeck deckOffset={deckOffset} />
                                                    <div className="tarot-board-center">
                                                        <CelticCrossLayout spreadCards={spreadCards} />
                                                        <CelticCrossControls onDeal={handleDealCelticCross} onGenerate={handleGenerateFromCelticCross} isDealing={isDealing} />
                                                    </div>
                                                    <DiscardPile discardPile={discardPile} />
                                                </div>
                                            }

                                            {selectedSpread === 'grid' && !results && (
                                                <div className="controls-wrapper">
                                                    <div className="dice-container">
                                                        <button className="dice-shuffle" onClick={handleAnimatedShuffle} disabled={isShuffling}>
                                                            🎲
                                                        </button>
                                                    </div>
                                                    <div className="button-group">
                                                        <button onClick={handleGenerateFromTarot} className="action-button" disabled={loading}>선택 결과 보기</button>
                                                        <button onClick={handleGenerateCutting} className="action-button" disabled={loading}>자동 결과 보기</button>
                                                    </div>
                                                    <button
                                                        className="help-icon"
                                                        data-tooltip={`마음을 차분하고 고요히 가라앉히고 카드를 응시합니다.\n원하는 만큼 카드를 셔플링(🎲) 한 다음, 마음이 이끄는 카드를 선택하여 조합을 생성할 수 있습니다.\n\n자동 결과 보기 버튼은 실제 타로점 보는 방식처럼 45장의 카드를 하나의 탑으로 쌓은 후 한 번씩, 총 6개의 탑으로 랜덤 커팅 후 놓인 각 탑의 최상위 첫 번째 카드가 선택됩니다.`}
                                                    >?</button>
                                                </div>
                                            )}

                                            {results && selectedSpread !== 'rv' && (
                                                <button onClick={handleAnimatedShuffle} className="action-button reset-button recommend-button">새 스프레드</button>
                                            )}

                                            <div ref={resultsContainerRef}>
                                                {loading && <p>분석 중...</p>}
                                                {error && <p className="error-message">{error}</p>}
                                                {results && selectedSpread !== 'rv' && <ResultDisplay activeTab={activeTab} results={results} selectedSpread={selectedSpread} resultSourceSpread={resultSourceSpread} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} majorMeanings={majorMeanings} />}
                                            </div>

                                            {/* 하단 3열 광고 */}
                                            <CoupangAdBottom products={coupangAdData.bottomProducts} partnerId={coupangAdData.partnerId} count={3} rotationKey={adRotationKey} />
                                        </div>
                                    </>
                                )}
                                {activeTab === 'analysis' && (
                                    <>
                                        {analysisMode !== 'history' && (
                                            <LatestDrawInfo
                                                drawInfo={latestDrawInfo}
                                                majorMeanings={majorMeanings}
                                                onMajorCardEnter={handleMajorCardMouseEnter}
                                                onMajorCardLeave={handleMouseLeave}
                                                onPrev={handlePrevDraw}
                                                onNext={handleNextDraw}
                                                onDateClick={() => setIsDrawDropdownOpen(!isDrawDropdownOpen)}
                                                isDropdownOpen={isDrawDropdownOpen}
                                                allDrawNumbers={Array.from({ length: maxDrwNo - minDrwNo + 1 }, (_, i) => maxDrwNo - i)}
                                                onDrawSelect={handleDrawSelect}
                                                dropdownRef={drawDropdownRef}
                                            />
                                        )}
                                        <div className="analysis-container">
                                            <div className="stats-panel">
                                                <AnalysisGlobalControls
                                                    startDrwNo={startDrwNo}
                                                    setStartDrwNo={setStartDrwNo}
                                                    endDrwNo={endDrwNo}
                                                    setEndDrwNo={setEndDrwNo}
                                                    minDrwNo={minDrwNo}
                                                    maxDrwNo={maxDrwNo}
                                                    includeBonus={includeBonus}
                                                    setIncludeBonus={setIncludeBonus}
                                                    onQuery={handleAnalysisQuery}
                                                    onHistoryQuery={handleWinningHistoryQuery}
                                                    onUpdate={handleManualUpdate}
                                                    updateStatus={updateStatus}
                                                    loading={loading}
                                                    quickRange={quickRange}
                                                    setQuickRange={setQuickRange}
                                                    analysisMode={analysisMode}
                                                />
                                                {analysisMode === 'history' ? (
                                                    <WinningHistoryList
                                                        historyData={winningHistory}
                                                        currentPage={historyPage}
                                                        totalPages={Math.ceil(winningHistory.length / 15)}
                                                        onPageChange={setHistoryPage}
                                                        majorMeanings={majorMeanings}
                                                    />
                                                ) : (
                                                    <>
                                                        <div className="panel-header">
                                                            <h3>통계 정보</h3>
                                                            <StatsSelector selectedStats={selectedStats} onSelect={handleStatSelection} onReset={handleStatsReset} />
                                                        </div>
                                                        <StatsDisplay
                                                            analysisData={analysisData}
                                                            loading={loading}
                                                            startDrwNo={startDrwNo}
                                                            endDrwNo={endDrwNo}
                                                            selectedStats={selectedStats}
                                                            includeBonus={includeBonus}
                                                            compatibilityQueryTrigger={compatibilityQueryTrigger}
                                                            regressionDetail={regressionDetail}
                                                            regressionDetailLoading={regressionDetailLoading}
                                                            regressionTarget={regressionTarget}
                                                            onRegressionNumberClick={handleRegressionNumberClick}
                                                            significantOnly={significantOnly}
                                                            setSignificantOnly={setSignificantOnly}
                                                        />
                                                        <CoupangAdBottom products={coupangAdData.bottomProducts} partnerId={coupangAdData.partnerId} rotationKey={adRotationKey} />
                                                    </>
                                                )}
                                            </div>

                                        </div>
                                    </>
                                )}
                                {activeTab === 'stats' && (
                                    <>
                                        <div className="container">
                                            <h2>로또 & 타로 필터 조합기</h2>
                                            <FilterControls filters={filters} setFilters={setFilters} onGenerate={handleGenerateFiltered} onReset={handleFilterReset} majorMeanings={majorMeanings} loading={loading} />
                                        </div>

                                        {/* Ad Placeholder: Interstitial - This would typically be a modal triggered by the generate button */}

                                        <div ref={resultsContainerRef} className="results-container-filters">
                                            {/* Ad Placeholder: Banner Top */}
                                            {/* <div className="ad-banner-placeholder">배너 광고 (상단)</div> */}

                                            {loading && <p>분석 중...</p>}
                                            {error && <p className="error-message">{error}</p>}
                                            {results && <ResultDisplay activeTab={activeTab} results={results} selectedSpread={selectedSpread} resultSourceSpread={resultSourceSpread} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} majorMeanings={majorMeanings} />}

                                            {/* Ad Placeholder: Banner Bottom */}
                                            <CoupangAdBottom products={coupangAdData.bottomProducts} partnerId={coupangAdData.partnerId} rotationKey={adRotationKey} />
                                        </div>
                                    </>
                                )}
                                {activeTab === 'gallery' && (
                                    <div className="container">
                                        <h2>갤러리(가칭)</h2>
                                        <p>공지사항,자유 게시판,이번주 추천번호샵,당첨인증샷,분석법 노하우 공유등</p>
                                    </div>
                                )}
                            </>
                        )}
                        {currentPage === 'terms' && <TermsOfService setCurrentPage={setCurrentPage} />}
                        {currentPage === 'privacy' && <PrivacyPolicy setCurrentPage={setCurrentPage} />}
                    </main>
                    {/* <div className="ad-placeholder-global-bottom" style={{ margin: '1rem 0', border: '1px dashed #4a4a70', padding: '4rem 1rem', backgroundColor: 'rgba(0,0,0,0.2)', textAlign: 'center', color: '#666' }}>하단 전역 배너</div> */}
                </div>

                {/* 우측 사이드바 광고 (3개) + 다이나믹 배너 + 검색 위젯 */}
                <CoupangAdSidebar
                    products={coupangAdData.rightSidebarProducts}
                    position="right"
                    count={3}
                    showSearchWidget={true}
                    dynamicBannerUrl="https://ads-partners.coupang.com/widgets.html?id=948347&template=carousel&trackingCode=AF5327763&subId=&width=200&height=200&tsource="
                />
            </div>

            <AppFooter setCurrentPage={setCurrentPage} />
            <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
            {/* {showInterstitial && 
                <InterstitialAdPlaceholder 
                    title="조합 생성"
                    onClose={() => {
                        setShowInterstitial(false);
                        executeGeneration();
                    }}
                />
            }
            {showTabInterstitial &&
                <InterstitialAdPlaceholder
                    title="탭 이동"
                    onClose={() => {
                        setShowTabInterstitial(false);
                        localStorage.setItem('lastTabAdShownTime', new Date().getTime().toString());
                        setCurrentPage('main');
                        setActiveTab(nextTab);
                    }}
                />
            } */}
            <button
                className={`scroll-to-top-button ${showScrollToTop ? 'visible' : ''}`}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                aria-label="맨 위로 스크롤"
            >
                ↑
            </button>
        </div >
    );
}

const StatsSelector = ({ selectedStats, onSelect, onReset }) => {
    return (
        <div className="stats-selector">
            {STAT_OPTIONS.map(option => (
                <button
                    key={option.id}
                    className={`stat-toggle-button ${selectedStats.includes(option.id) ? 'active' : ''}`}
                    onClick={() => onSelect(option.id)}
                    data-tooltip={STAT_DESCRIPTIONS[option.id] || ''}
                >
                    {option.name}
                </button>
            ))}
            <button onClick={onReset} className="stat-toggle-button reset-stat-button">초기화</button>
        </div>
    );
};

const AnalysisGlobalControls = ({
    startDrwNo, setStartDrwNo, endDrwNo, setEndDrwNo,
    minDrwNo, maxDrwNo,
    includeBonus, setIncludeBonus,
    onQuery, onHistoryQuery, onUpdate, updateStatus, loading,
    quickRange, setQuickRange
}) => {

    const setRecentWeeks = (weeks) => {
        if (weeks === "") return;
        const numWeeks = Number(weeks);
        if (numWeeks === 0) {
            setStartDrwNo(minDrwNo);
            setEndDrwNo(maxDrwNo);
        } else {
            setStartDrwNo(Math.max(minDrwNo, maxDrwNo - numWeeks + 1));
            setEndDrwNo(maxDrwNo);
        }
    };

    const drawNumbers = [];
    for (let i = maxDrwNo; i >= minDrwNo; i--) {
        drawNumbers.push(i);
    }

    return (
        <div className="global-controls-wrapper">
            <div className="global-controls">
                <div className="control-section">
                    <div className="control-group draw-range-selector">
                        <label>조회 범위:</label>
                        <select className="draw-range-select" value={startDrwNo} onChange={(e) => setStartDrwNo(Number(e.target.value))}>
                            {drawNumbers.map(num =>
                                <option key={`start-${num}`} value={num} disabled={num > endDrwNo}>{num}</option>
                            )}
                        </select>
                        <span>~</span>
                        <select className="draw-range-select" value={endDrwNo} onChange={(e) => setEndDrwNo(Number(e.target.value))}>
                            {drawNumbers.map(num =>
                                <option key={`end-${num}`} value={num} disabled={num < startDrwNo}>{num}</option>
                            )}
                        </select>
                    </div>
                    <div className="control-group">
                        <select value={quickRange} onChange={(e) => { setRecentWeeks(e.target.value); setQuickRange(e.target.value); }} className="quick-range-select">
                            <option value="">기간 선택</option>
                            <option value="4">최근 1개월</option>
                            <option value="13">최근 3개월</option>
                            <option value="26">최근 6개월</option>
                            <option value="52">최근 1년</option>
                            <option value="0">전체 기간</option>
                        </select>
                    </div>
                    <div className="control-group bonus-ball-toggle">
                        <label>
                            <input type="checkbox" checked={includeBonus} onChange={(e) => setIncludeBonus(e.target.checked)} />
                            보너스 볼 포함
                        </label>
                    </div>
                    <button onClick={onQuery} className="action-button query-button" disabled={loading}>
                        📊 차트 조회
                    </button>
                    <div className="vertical-separator"></div>
                    <button onClick={onHistoryQuery} className="action-button query-button" disabled={loading}>
                        📜 당첨번호 조회
                    </button>
                </div>
                {/* <div className="action-section">
                <button onClick={onUpdate} disabled={updateStatus.loading} className="action-button update-button">
                    🔄 DB 업데이트
                </button>
            </div> */}
            </div>
            {updateStatus.message &&
                <div className="update-status-section">
                    <p className={`update-status ${updateStatus.error ? 'error' : ''}`}>
                        {updateStatus.message}
                    </p>
                </div>
            }
        </div>
    );
};

const SpreadSelector = ({ selectedSpread, setSelectedSpread }) => {
    return (
        <div className="spread-selector">
            <button
                className={`spread-button hover-levitate ${selectedSpread === 'rv' ? 'active' : ''}`}
                onClick={() => setSelectedSpread('rv')}
            >
                🔮 리모트 뷰잉
            </button>
            <button
                className={`spread-button hover-levitate ${selectedSpread === 'grid' ? 'active' : ''}`}
                onClick={() => setSelectedSpread('grid')}
            >
                ✨ 목표 달성
            </button>
            <button
                className={`spread-button hover-levitate ${selectedSpread === 'celticCross' ? 'active' : ''}`}
                onClick={() => setSelectedSpread('celticCross')}
            >
                ♰ 켈틱 크로스
            </button>
        </div>
    );
};

const CelticCrossLayout = ({ spreadCards }) => {
    return (
        <div className="celtic-cross-layout">
            {[...Array(10)].map((_, i) => (
                <div key={i} className={`celtic-card-slot celtic-card-slot-${i + 1}`}>
                    {spreadCards[i] ?
                        <img src={`${process.env.PUBLIC_URL}/Cards-png/CardBacks.png`} alt="Card Back" /> :
                        (i + 1)
                    }
                </div>
            ))}
        </div>
    );
};

const SourceDeck = ({ deckOffset }) => {
    return (
        <div className="source-deck-pile">
            <h4>남은 카드</h4>
            <div className="card-stack">
                {[...Array(5)].map((_, i) => <div key={i} className="stacked-card"></div>)}
            </div>
            <p className="card-count">{45 - deckOffset} / 45</p>
        </div>
    );
};

const DiscardPile = ({ discardPile }) => {
    return (
        <div className="discard-pile">
            <h4>버린 카드</h4>
            <div className="discard-grid">
                {discardPile.length > 0 ? (
                    discardPile.slice(0, 10).map(num => (
                        <div key={num} className="discarded-card">
                            <img src={`${process.env.PUBLIC_URL}/Cards-png/${getCardImageName(num)}`} alt={`Card ${num}`} />
                        </div>
                    ))
                ) : null}
            </div>
            <p className="card-count">{discardPile.length} / 40</p>
        </div>
    );
};

const CelticCrossControls = ({ onDeal, onGenerate, isDealing }) => {
    return (
        <div className="celtic-cross-controls">
            <button className="dice-shuffle" onClick={onDeal} disabled={isDealing}><FontAwesomeIcon icon="arrows-rotate" /></button>
            <button className="action-button" onClick={onGenerate}>결과 보기</button>
            <button
                className="help-icon"
                data-tooltip={`🔄을 누르면 셔플 된 덱의 맨 위에서부터 10장을 순서대로 각 카드 슬롯에 배치합니다 다시 🔄을 누르면 슬롯의 카드는 버려지고 남은 카드 더미에서 새로 10장이 배치됩니다.\n마음에 드는 카드 배열이 되었을 때 '결과 보기' 버튼을 누르면 1~6번 슬롯 위치의 카드가 번호 조합에 사용됩니다.`}
            >?</button>
        </div>
    );
};

const LoginModal = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    const handleModalContentClick = (e) => {
        e.stopPropagation();
    };

    const handleLogin = (e) => {
        e.preventDefault();
        // 로그인 로직 (추후 구현)
        alert('로그인 되었습니다.');
        onClose();
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={handleModalContentClick}>
                <button className="modal-close-button" onClick={onClose}>&times;</button>
                <h2>로그인</h2>
                <form onSubmit={handleLogin} className="login-form">
                    <div className="form-group">
                        <label htmlFor="username">아이디</label>
                        <input type="text" id="username" name="username" required />
                    </div>
                    <div className="form-group">
                        <label htmlFor="password">비밀번호</label>
                        <input type="password" id="password" name="password" required />
                    </div>
                    <div className="form-actions">
                        <button type="submit" className="action-button">로그인</button>
                        <button type="button" className="action-button secondary" onClick={onClose}>취소</button>
                    </div>
                </form>
                <div className="form-links">
                    <a href="#! ">아이디 찾기</a>
                    <span>|</span>
                    <a href="#! ">비밀번호 찾기</a>
                    <span>|</span>
                    <a href="#! ">회원가입</a>
                </div>
            </div>
        </div>
    );
};

const ResultDisplay = ({ results, activeTab, selectedSpread, resultSourceSpread, onMouseEnter, onMouseLeave, majorMeanings }) => {
    const [revealed, setRevealed] = useState(false);

    useEffect(() => {
        setRevealed(false);
        const timer = setTimeout(() => setRevealed(true), 100);
        return () => clearTimeout(timer);
    }, [results]);

    const data = results.generated_numbers || [];
    if (data.length === 0) {
        return <p>조건에 맞는 번호 조합을 찾지 못했습니다.</p>;
    }

    return data.map((item, index) => (
        <div key={index}>
            <RandomDynamicBanner />
            <LottoSet set={item} index={index} activeTab={activeTab} selectedSpread={selectedSpread} resultSourceSpread={resultSourceSpread} revealed={revealed} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} />
            {item.major_arcana_card !== undefined && majorMeanings &&
                <MajorArcanaDisplay cardNumber={item.major_arcana_card} meanings={majorMeanings} />
            }
        </div>
    ));
};

const MajorArcanaDisplay = ({ cardNumber, meanings }) => {
    const cardData = meanings[cardNumber];
    if (!cardData) return null;

    const cardImageName = (() => {
        const numStr = String(cardNumber).padStart(2, '0');
        const nameStr = cardData.name.replace(/ /g, '');
        return `${numStr}-${nameStr}.png`;
    })();

    return (
        <div className="major-arcana-display">
            <h3>조합의 상징: {cardData.korean_name}</h3>
            <div className="major-arcana-content">
                <img src={`${process.env.PUBLIC_URL}/Cards-png/${cardImageName}`} alt={cardData.name} />
                <div className="meaning">
                    <h4>{cardData.name}</h4>
                    <p>{cardData.description}</p>
                </div>
            </div>
        </div>
    );
};

const LottoSet = ({ set, revealed, onMouseEnter, onMouseLeave, index, activeTab, selectedSpread, resultSourceSpread }) => {
    if (!set || !set.numbers) {
        return null;
    }

    return (
        <div className={`lotto-set ${revealed ? 'revealed' : ''}`}>
            <div className="card-display-area">
                {set.numbers.map((num, cardIndex) => (
                    <div key={num} className="result-card-container" onMouseEnter={(e) => onMouseEnter(num, e)} onMouseLeave={onMouseLeave}>
                        <div className="result-card">
                            <div className="card-front">
                                <img src={`${process.env.PUBLIC_URL}/Cards-png/${getCardImageName(num)}`} alt={`Card ${num}`} />
                            </div>
                            <div className="card-back">
                                <img src={`${process.env.PUBLIC_URL}/Cards-png/CardBacks.png`} alt="Card Back" />
                            </div>
                        </div>
                        <p className={`card-number ${getLottoColorClass(num)}`}>{num}</p>
                        {activeTab === 'tarot' && resultSourceSpread === 'grid' && (
                            <div className="card-position-meaning">
                                <strong>{GOAL_ACHIEVEMENT_SPREAD[cardIndex]?.title}</strong>
                                <span>{GOAL_ACHIEVEMENT_SPREAD[cardIndex]?.description}</span>
                            </div>
                        )}
                        {activeTab === 'tarot' && resultSourceSpread === 'celticCross' && (
                            <div className="card-position-meaning">
                                <strong>{CELTIC_CROSS_SPREAD[cardIndex]?.title}</strong>
                                <span>{CELTIC_CROSS_SPREAD[cardIndex]?.description}</span>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <p className="details">
                {set.win_history && <span className="rank-info"><strong>역대 당첨:</strong> {set.win_history}</span>}
                {set.win_history && " | "}
                {set.total_sum && <span><strong>합계:</strong> {set.total_sum}</span>}
                {set.total_sum && " | "}
                {set.odd_even_ratio && <span><strong>홀짝:</strong> {set.odd_even_ratio}</span>}
                {set.odd_even_ratio && " | "}
                {set.high_low_ratio && <span><strong>고저:</strong> {set.high_low_ratio}</span>}
                {set.ac_value !== undefined && " | "}
                {set.ac_value !== undefined && <span><strong>AC:값</strong> {set.ac_value}</span>}
                {set.elemental_balance && " | "}
                {set.elemental_balance && <span><strong>원소:</strong> 💨{set.elemental_balance.air} 💧{set.elemental_balance.water} 🔥{set.elemental_balance.fire} 🌿{set.elemental_balance.earth} ✨{set.elemental_balance.spirit}</span>}
            </p>
        </div>
    );
};

const TarotCardSelector = ({ cards, selectedCards, onCardClick, isShuffling }) => {
    return (
        <div className={`card-grid ${isShuffling ? 'shuffling' : ''}`}>
            {cards.map(num => (
                <div
                    key={num}
                    className={`card ${selectedCards.includes(num) ? 'selected' : ''}`}
                    style={{
                        backgroundImage: `url(${process.env.PUBLIC_URL}/Cards-png/CardBacks.png)`
                    }}
                    onClick={() => onCardClick(num)}
                >
                </div>
            ))}
        </div>
    );
};

const StatsDisplay = ({ analysisData, loading, startDrwNo, endDrwNo, selectedStats, includeBonus, compatibilityQueryTrigger, regressionDetail, regressionDetailLoading, regressionTarget, onRegressionNumberClick, significantOnly, setSignificantOnly }) => {
    const [compatibilityTarget, setCompatibilityTarget] = useState(null);
    const [compatibilityData, setCompatibilityData] = useState(null);
    const [compatibilityLoading, setCompatibilityLoading] = useState(false);
    const sectionRefs = useRef({});
    const prevSelectedStats = useRef(selectedStats);
    const [scrollTo, setScrollTo] = useState(null);

    // Effect to determine the scroll target
    useEffect(() => {
        const newlySelected = selectedStats.filter(stat => !prevSelectedStats.current.includes(stat));
        if (newlySelected.length > 0) {
            const lastSelectedStat = newlySelected[newlySelected.length - 1];
            setScrollTo(lastSelectedStat);
        }
        prevSelectedStats.current = selectedStats;
    }, [selectedStats]);

    // Effect to execute scrolling once the target is rendered
    useEffect(() => {
        if (scrollTo && sectionRefs.current[scrollTo]) {
            const ref = sectionRefs.current[scrollTo];
            setTimeout(() => {
                ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
                setScrollTo(null); // Reset after scrolling
            }, 100); // Short delay to ensure rendering is complete
        }
    }, [scrollTo, analysisData]); // Re-run when data changes, giving the ref a chance to appear

    const handleCompatibilityQuery = useCallback(async (target, forceRefresh = false) => {
        if (target === compatibilityTarget && !forceRefresh) return;

        setCompatibilityTarget(target);
        setCompatibilityLoading(true);
        setCompatibilityData(null);
        try {
            const params = {
                target_number: target,
                start_drw_no: startDrwNo,
                end_drw_no: endDrwNo,
                include_bonus: includeBonus
            };
            const response = await axios.get(`${API_URL}/api/analysis/compatibility-numbers`, { params });
            setCompatibilityData(response.data);
        } catch (error) {
            console.error("Failed to fetch compatibility data:", error);
            alert('궁합수 데이터를 불러오는 데 실패했습니다.');
        } finally {
            setCompatibilityLoading(false);
        }
    }, [startDrwNo, endDrwNo, includeBonus, compatibilityTarget]);

    useEffect(() => {
        // This effect is ONLY for the trigger from the main "Query" button.
        // It should not run on the initial render (trigger === 0).
        if (compatibilityQueryTrigger > 0 && compatibilityTarget !== null) {
            // Re-run the query for the currently selected target number.
            handleCompatibilityQuery(compatibilityTarget, true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [compatibilityQueryTrigger]);

    const hasData = Object.keys(analysisData).length > 0;

    if (selectedStats.length === 0) {
        return (
            <div className="stats-content empty">
                <p>표시할 통계 항목을 선택하세요.</p>
            </div>
        );
    }

    if (loading && !hasData) {
        return (
            <div className="stats-content loading">
                <p>데이터를 불러오는 중...</p>
            </div>
        );
    }

    if (!hasData && !loading) {
        return (
            <div className="stats-content empty">
                <p>선택된 기간에 대한 데이터가 없습니다. 다른 기간을 조회해보세요.</p>
            </div>
        );
    }

    const { hotCold, sumRange, treeRatios, arcanaDistribution, oddEvenRatio, highLowRatio, appearancesByNumber, elementalBalance, consecutiveNumbers, nonAppearance, endingDigits, acValue, isomorphicNumbers, regressionTrends } = analysisData;

    const regressionDetailData = (regressionDetail && regressionDetail.draw_numbers) ? {
        datasets: [
            {
                type: 'scatter',
                label: '출현',
                data: regressionDetail.draw_numbers.map((dn, i) => (
                    regressionDetail.appearances[i] === 1 ? { x: dn, y: 1 } : null
                )).filter(d => d !== null),
                backgroundColor: 'rgba(255, 99, 132, 0.7)',
                pointRadius: 5,
                clip: false,
            },
            {
                type: 'line',
                label: '추세선',
                data: regressionDetail.draw_numbers.map((dn, i) => (
                    { x: dn, y: regressionDetail.trendline[i] }
                )),
                borderColor: 'rgba(75, 192, 192, 1)',
                backgroundColor: 'rgba(75, 192, 192, 0.2)',
                borderWidth: 2,
                pointRadius: 0,
                fill: false,
            }
        ]
    } : null;

    const regressionDetailOptions = {
        responsive: true,
        plugins: {
            legend: { position: 'top' },
            title: { display: true, text: `${regressionTarget}번 출현 빈도 및 추세` },
            tooltip: {
                mode: 'index',
                intersect: false,
            },
        },
        scales: {
            x: {
                min: (regressionDetail && regressionDetail.draw_numbers) ? Math.min(...regressionDetail.draw_numbers) : startDrwNo,
                max: (regressionDetail && regressionDetail.draw_numbers) ? Math.max(...regressionDetail.draw_numbers) : endDrwNo,
            },
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 1,
                    callback: function (value) {
                        if (value === 0) return '미출현';
                        if (value === 1) return '출현';
                        return null;
                    }
                }
            }
        }
    };

    const sumRangeData = (sumRange && sumRange.top_sum_ranges) ? {
        labels: Object.keys(sumRange.top_sum_ranges),
        datasets: [{ label: '출현 횟수', data: Object.values(sumRange.top_sum_ranges), backgroundColor: 'rgba(75, 192, 192, 0.6)', borderColor: 'rgba(75, 192, 192, 1)', borderWidth: 1, },],
    } : null;

    const sumRangeOptions = { responsive: true, plugins: { legend: { position: 'top' }, title: { display: true, text: '최다 출현 총합 구간 (Top 5)' }, }, };

    const treeNameMap = {
        'AB': '1번 트리',
        'CD': '2번 트리',
        'EF': '3번 트리',
        'GH': '4번 트리',
        'IJ': '5번 트리'
    };

    const treeRatioData = (treeRatios && treeRatios.tree_ratios) ? {
        labels: treeRatios.tree_ratios.map(r => treeNameMap[r.tree_name] || r.tree_name),
        datasets: [{ label: '', data: treeRatios.tree_ratios.map(r => r.percentage), backgroundColor: ['rgba(255, 99, 132, 0.6)', 'rgba(54, 162, 235, 0.6)', 'rgba(255, 206, 86, 0.6)', 'rgba(75, 192, 192, 0.6)', 'rgba(153, 102, 255, 0.6)'], borderColor: ['rgba(255, 99, 132, 1)', 'rgba(54, 162, 235, 1)', 'rgba(255, 206, 86, 1)', 'rgba(75, 192, 192, 1)', 'rgba(153, 102, 255, 1)'], borderWidth: 1, },],
    } : null;

    const treeRatioOptions = { responsive: true, plugins: { legend: { position: 'top', labels: { font: { size: (ctx) => ctx.chart.width / 56 } } }, title: { display: true, text: '전체 가짓수 : 트리별 당첨 비율 (%)' }, }, };

    const arcanaData = (arcanaDistribution && arcanaDistribution.arcana_distribution) ? (() => {
        const distribution = [...arcanaDistribution.arcana_distribution].sort((a, b) => b.count - a.count);

        // Get unique count values, sorted descending
        const uniqueCounts = [...new Set(distribution.map(d => d.count))].sort((a, b) => b - a);

        // Determine the threshold counts for top and bottom 3 unique values
        const top3_threshold = uniqueCounts.length > 2 ? uniqueCounts[2] : (uniqueCounts[0] || 0);
        const bottom3_threshold = uniqueCounts.length > 2 ? uniqueCounts[uniqueCounts.length - 3] : (uniqueCounts[uniqueCounts.length - 1] || 0);

        const backgroundColors = arcanaDistribution.arcana_distribution.map(d => {
            // Handle cases where there are fewer than 3 unique counts
            if (uniqueCounts.length < 3) {
                if (d.count === uniqueCounts[0]) return 'rgba(255, 99, 132, 0.7)'; // Highlight only the top
                if (d.count === uniqueCounts[uniqueCounts.length - 1] && uniqueCounts.length > 1) return 'rgba(54, 162, 235, 0.7)'; // And the bottom if it exists
                return 'rgba(255, 159, 64, 0.6)';
            }

            if (d.count >= top3_threshold) return 'rgba(255, 99, 132, 0.7)'; // Hot color
            if (d.count <= bottom3_threshold) return 'rgba(54, 162, 235, 0.7)'; // Cold color
            return 'rgba(255, 159, 64, 0.6)'; // Default color
        });

        return {
            labels: arcanaDistribution.arcana_distribution.map(d => d.card_name),
            datasets: [{
                label: '출현 횟수',
                data: arcanaDistribution.arcana_distribution.map(d => d.count),
                backgroundColor: backgroundColors,
                borderColor: 'rgba(255, 159, 64, 1)',
                borderWidth: 1,
            },],
        };
    })() : null;

    const arcanaOptions = { responsive: true, plugins: { legend: { display: false }, title: { display: true, text: '메이저 아르카나별 출현 빈도', }, }, scales: { x: { ticks: { autoSkip: false, maxRotation: 90, minRotation: 45 } } } };

    const oddEvenData = oddEvenRatio ? {
        labels: Object.keys(oddEvenRatio),
        datasets: [{ label: '출현 횟수', data: Object.values(oddEvenRatio), backgroundColor: 'rgba(153, 102, 255, 0.6)', borderColor: 'rgba(153, 102, 255, 1)', borderWidth: 1, },],
    } : null;

    const oddEvenOptions = { responsive: true, plugins: { legend: { position: 'top' }, title: { display: true, text: '홀/짝 비율별 출현 횟수' }, }, };

    const highLowData = highLowRatio ? {
        labels: Object.keys(highLowRatio),
        datasets: [{ label: '출현 횟수', data: Object.values(highLowRatio), backgroundColor: 'rgba(255, 99, 132, 0.6)', borderColor: 'rgba(255, 99, 132, 1)', borderWidth: 1, },],
    } : null;

    const highLowOptions = { responsive: true, plugins: { legend: { position: 'top' }, title: { display: true, text: '고/저 비율별 출현 횟수' }, }, };

    const appearancesData = appearancesByNumber ? {
        labels: Object.keys(appearancesByNumber),
        datasets: [{ label: '출현 횟수', data: Object.values(appearancesByNumber), backgroundColor: 'rgba(54, 162, 235, 0.6)', borderColor: 'rgba(54, 162, 235, 1)', borderWidth: 1, },],
    } : null;

    const appearancesOptions = {
        responsive: true,
        plugins: {
            legend: { display: false },
            title: { display: true, text: '번호별 출현 횟수' },
        },
        // scales.x.ticks.color is now handled globally
    };

    const elementalBalanceData = elementalBalance ? {
        labels: ['공기(1💨10)', '물(11💧20)', '불(21🔥30)', '흙(31🌿40)', '아카샤(41✨45)'],
        datasets: [{
            label: '출현 횟수',
            data: [
                elementalBalance.air,
                elementalBalance.water,
                elementalBalance.fire,
                elementalBalance.earth,
                elementalBalance.spirit
            ],
            backgroundColor: [
                'rgba(173, 216, 230, 0.6)',
                'rgba(54, 162, 235, 0.6)',
                'rgba(255, 99, 132, 0.6)',
                'rgba(152, 251, 152, 0.6)',
                'rgba(153, 102, 255, 0.6)'
            ],
            borderColor: [
                'rgba(173, 216, 230, 1)',
                'rgba(54, 162, 235, 1)',
                'rgba(255, 99, 132, 1)',
                'rgba(152, 251, 152, 1)',
                'rgba(153, 102, 255, 1)'
            ],
            borderWidth: 1,
        },],
    } : null;

    const elementalBalanceOptions = { responsive: true, plugins: { legend: { position: 'top', labels: { font: { size: (ctx) => ctx.chart.width / 52 } } }, title: { display: true, text: '오행 원소별 총 출현 횟수' }, }, };

    const consecutiveNumbersData = consecutiveNumbers ? {
        labels: Object.keys(consecutiveNumbers),
        datasets: [{ label: '출현 횟수', data: Object.values(consecutiveNumbers), backgroundColor: 'rgba(75, 192, 192, 0.6)', borderColor: 'rgba(75, 192, 192, 1)', borderWidth: 1, },],
    } : null;

    const consecutiveNumbersOptions = { responsive: true, plugins: { legend: { position: 'top' }, title: { display: true, text: '연속 번호 출현 분포' }, }, };

    const endingDigitsData = endingDigits ? {
        labels: Object.keys(endingDigits),
        datasets: [{ label: '출현 횟수', data: Object.values(endingDigits), backgroundColor: 'rgba(255, 206, 86, 0.6)', borderColor: 'rgba(255, 206, 86, 1)', borderWidth: 1, },],
    } : null;

    const endingDigitsOptions = { responsive: true, plugins: { legend: { position: 'top' }, title: { display: true, text: '끝수별 출현 횟수' }, }, };

    const acValueData = acValue ? {
        labels: Object.keys(acValue),
        datasets: [{ label: '출현 횟수', data: Object.values(acValue), backgroundColor: 'rgba(255, 159, 64, 0.6)', borderColor: 'rgba(255, 159, 64, 1)', borderWidth: 1, },],
    } : null;

    const acValueOptions = { responsive: true, plugins: { legend: { position: 'top' }, title: { display: true, text: 'AC값 분포' }, }, };

    const mirrorNumbersData = (isomorphicNumbers && isomorphicNumbers.mirror_numbers) ? {
        labels: Object.keys(isomorphicNumbers.mirror_numbers),
        datasets: [{ label: '동시 출현 횟수', data: Object.values(isomorphicNumbers.mirror_numbers), backgroundColor: 'rgba(75, 192, 192, 0.6)', borderColor: 'rgba(75, 192, 192, 1)', borderWidth: 1, },],
    } : null;

    const mirrorNumbersOptions = { responsive: true, plugins: { legend: { display: false }, title: { display: true, text: '거울수 동시 출현' }, }, };

    const sameEndingNumbersData = (isomorphicNumbers && isomorphicNumbers.same_ending_numbers) ? {
        labels: Object.keys(isomorphicNumbers.same_ending_numbers),
        datasets: [{ label: '출현한 총 회차 수', data: Object.values(isomorphicNumbers.same_ending_numbers), backgroundColor: 'rgba(255, 159, 64, 0.6)', borderColor: 'rgba(255, 159, 64, 1)', borderWidth: 1, },],
    } : null;

    const sameEndingNumbersOptions = { responsive: true, plugins: { legend: { position: 'top' }, title: { display: true, text: '동끝수 출현 분포' }, }, };

    const compatibilityChartData = (compatibilityData) ? {
        labels: Object.keys(compatibilityData),
        datasets: [{ label: `'${compatibilityTarget}'번과 함께 나온 횟수`, data: Object.values(compatibilityData), backgroundColor: 'rgba(153, 102, 255, 0.6)', borderColor: 'rgba(153, 102, 255, 1)', borderWidth: 1, },],
    } : {
        labels: [],
        datasets: [{ label: '궁합수', data: [], backgroundColor: 'rgba(153, 102, 255, 0.6)' }],
    };

    const compatibilityOptions = { responsive: true, plugins: { legend: { display: false }, title: { display: true, text: compatibilityTarget ? `'${compatibilityTarget}'번의 궁합수 (Top 10)` : '궁합수' }, }, };

    return (
        <div className="stats-content">
            {loading && (
                <div className="loading-overlay">
                    <p>새로고침...</p>
                </div>
            )}

            {selectedStats.includes('hot_cold') && hotCold && (
                <>
                    <RandomDynamicBanner />
                    <div ref={el => sectionRefs.current['hot_cold'] = el}>
                        <div className="stats-section">
                            <h4>🔥 핫 넘버 ({startDrwNo}회~{endDrwNo}회)</h4>
                            <div className="number-grid">
                                {hotCold.hot_numbers.map(item => (
                                    <div key={item.number} className="number-cell-container">
                                        <span className={`number-cell hot`}>{item.number}</span>
                                        <span className="number-count">{item.count}회</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="stats-section">
                            <h4>❄️ 콜드 넘버 ({startDrwNo}회~{endDrwNo}회)</h4>
                            <div className="number-grid">
                                {hotCold.cold_numbers.map(n => <span key={n} className={`number-cell cold`}>{n}</span>)}
                            </div>
                        </div>
                    </div>
                </>
            )}

            {selectedStats.includes('total_top5') && sumRangeData && (
                <>
                    <RandomDynamicBanner />
                    <div className="stats-section" ref={el => sectionRefs.current['total_top5'] = el}>
                        <Bar options={sumRangeOptions} data={sumRangeData} />
                    </div>
                </>
            )}

            {selectedStats.includes('elemental_tree') && treeRatioData && (
                <>
                    <RandomDynamicBanner />
                    <div className="stats-section" ref={el => sectionRefs.current['elemental_tree'] = el}>
                        <div className="chart-container-pie">
                            <Pie options={treeRatioOptions} data={treeRatioData} />
                        </div>
                    </div>
                </>
            )}

            {selectedStats.includes('arcana') && arcanaData && (
                <>
                    <RandomDynamicBanner />
                    <div className="stats-section" ref={el => sectionRefs.current['arcana'] = el}>
                        <Bar options={arcanaOptions} data={arcanaData} />
                    </div>
                </>
            )}

            {selectedStats.includes('odd_even_ratio') && oddEvenData && (
                <>
                    <RandomDynamicBanner />
                    <div className="stats-section" ref={el => sectionRefs.current['odd_even_ratio'] = el}>
                        <Bar options={oddEvenOptions} data={oddEvenData} />
                    </div>
                </>
            )}

            {selectedStats.includes('high_low_ratio') && highLowData && (
                <>
                    <RandomDynamicBanner />
                    <div className="stats-section" ref={el => sectionRefs.current['high_low_ratio'] = el}>
                        <Bar options={highLowOptions} data={highLowData} />
                    </div>
                </>
            )}

            {selectedStats.includes('appearances_by_number') && appearancesData && (
                <>
                    <RandomDynamicBanner />
                    <div className="stats-section" ref={el => sectionRefs.current['appearances_by_number'] = el}>
                        <Bar options={appearancesOptions} data={appearancesData} />
                    </div>
                </>
            )}

            {selectedStats.includes('elemental_balance') && elementalBalanceData && (
                <>
                    <RandomDynamicBanner />
                    <div className="stats-section" ref={el => sectionRefs.current['elemental_balance'] = el}>
                        <div className="chart-container-pie">
                            <Pie options={elementalBalanceOptions} data={elementalBalanceData} />
                        </div>
                    </div>
                </>
            )}

            {selectedStats.includes('consecutive_numbers') && consecutiveNumbersData && (
                <>
                    <RandomDynamicBanner />
                    <div className="stats-section" ref={el => sectionRefs.current['consecutive_numbers'] = el}>
                        <Bar options={consecutiveNumbersOptions} data={consecutiveNumbersData} />
                    </div>
                </>
            )}

            {selectedStats.includes('non_appearance') && nonAppearance && (
                <>
                    <RandomDynamicBanner />
                    <div className="stats-section" ref={el => sectionRefs.current['non_appearance'] = el}>
                        <h4>📅 장기 미출현 번호 (Top 15)</h4>
                        <div className="non-appearance-grid">
                            {nonAppearance.non_appearing_numbers.map(item => (
                                <div key={item.number} className="non-appearance-item">
                                    <span className="number-cell cold">{item.number}</span>
                                    <div className="non-appearance-details">
                                        <span className="non-appearance-period">{item.non_appearance_period}회 동안 미출현</span>
                                        <span className="last-appearance-draw">(마지막: {item.last_appearance_draw}회)</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}

            {selectedStats.includes('ending_digits') && endingDigitsData && (
                <>
                    <RandomDynamicBanner />
                    <div className="stats-section" ref={el => sectionRefs.current['ending_digits'] = el}>
                        <Bar options={endingDigitsOptions} data={endingDigitsData} />
                    </div>
                </>
            )}

            {selectedStats.includes('compatibility_numbers') && (
                <>
                    <RandomDynamicBanner />
                    <div className="stats-section" ref={el => sectionRefs.current['compatibility_numbers'] = el}>
                        <h4>🤝 궁합수 통계</h4>
                        <div className="number-grid compatibility-grid">
                            {Array.from({ length: 45 }, (_, i) => i + 1).map(num => (
                                <span
                                    key={num}
                                    className={`number-cell compatibility-cell ${getLottoColorClass(num)} ${compatibilityTarget === num ? 'selected' : ''}`}
                                    onClick={() => handleCompatibilityQuery(num, false)}
                                >
                                    {num}
                                </span>
                            ))}
                        </div>
                        <div className="compatibility-results">
                            <div style={{ position: 'relative' }}>
                                {compatibilityLoading && (
                                    <div className="loading-overlay" style={{ borderRadius: '8px' }}></div>
                                )}
                                <Bar options={compatibilityOptions} data={compatibilityChartData} />
                                {!compatibilityTarget && !compatibilityLoading && (
                                    <p className="chart-placeholder-message">궁합수를 분석할 번호를 선택하세요.</p>
                                )}
                                {compatibilityTarget && !compatibilityLoading && compatibilityData && Object.keys(compatibilityData).length === 0 && (
                                    <p className="chart-placeholder-message">선택한 번호에 대한 궁합수 데이터가 없습니다.</p>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}

            {selectedStats.includes('ac_value') && acValueData && (
                <>
                    <RandomDynamicBanner />
                    <div className="stats-section" ref={el => sectionRefs.current['ac_value'] = el}>
                        <Bar options={acValueOptions} data={acValueData} />
                    </div>
                </>
            )}

            {selectedStats.includes('isomorphic_numbers') && isomorphicNumbers && (
                <>
                    <RandomDynamicBanner />
                    <div className="stats-section" ref={el => sectionRefs.current['isomorphic_numbers'] = el}>
                        {mirrorNumbersData &&
                            <div className="sub-stats-section">
                                <Bar options={mirrorNumbersOptions} data={mirrorNumbersData} />
                            </div>
                        }
                        {sameEndingNumbersData &&
                            <div className="sub-stats-section">
                                <Bar options={sameEndingNumbersOptions} data={sameEndingNumbersData} />
                            </div>
                        }
                    </div>
                </>
            )}

            {selectedStats.includes('regression_trends') && regressionTrends && (
                <>
                    <RandomDynamicBanner />
                    <div ref={el => sectionRefs.current['regression_trends'] = el}>
                        <p className="stats-instruction">목록의 번호를 클릭하면 상세 추세 차트를 보거나 숨길 수 있습니다.</p>
                        <div className="stats-options">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={significantOnly}
                                    onChange={(e) => setSignificantOnly(e.target.checked)}
                                />
                                통계적으로 유의미한(p-value &lt; 0.05) 추세만 보기
                            </label>
                        </div>
                        <div className="stats-section">
                            <h4>📈 상승 추세 번호 (Top 5)</h4>
                            <div className="number-grid">
                                {regressionTrends.trending_up
                                    .filter(item => !significantOnly || item.pvalue < 0.05)
                                    .map(item => (
                                        <div key={item.number} className="number-cell-container trend-up" onClick={() => onRegressionNumberClick(item.number)}>
                                            <span className={`number-cell ${regressionTarget === item.number ? 'selected' : ''}`}>{item.number}</span>
                                            <span className="number-slope">+{item.slope.toFixed(6)}</span>
                                            <span className="number-pvalue">p: {item.pvalue.toFixed(4)}</span>
                                        </div>
                                    ))}
                            </div>
                        </div>
                        <div className="stats-section">
                            <h4>📉 하락 추세 번호 (Top 5)</h4>
                            <div className="number-grid">
                                {regressionTrends.trending_down
                                    .filter(item => !significantOnly || item.pvalue < 0.05)
                                    .map(item => (
                                        <div key={item.number} className="number-cell-container trend-down" onClick={() => onRegressionNumberClick(item.number)}>
                                            <span className={`number-cell ${regressionTarget === item.number ? 'selected' : ''}`}>{item.number}</span>
                                            <span className="number-slope">{item.slope.toFixed(6)}</span>
                                            <span className="number-pvalue">p: {item.pvalue.toFixed(4)}</span>
                                        </div>
                                    ))}
                            </div>
                        </div>
                        {regressionDetailLoading && <p>상세 정보 로딩 중...</p>}
                        {regressionDetailData && (
                            <div className="stats-section regression-detail-chart">
                                <Scatter options={regressionDetailOptions} data={regressionDetailData} />
                            </div>
                        )}
                    </div>
                </>
            )}

        </div>
    );
};

const NumberGrid = ({ filters, onNumberClick, selectionMode }) => {
    return (
        <div className="number-grid-filter">
            {Array.from({ length: 45 }, (_, i) => i + 1).map(num => {
                const isFixed = filters.fixedNumbers.includes(num);
                const isExcluded = filters.excludedNumbers.includes(num);
                let className = 'number-cell';
                if (isFixed) className += ' fixed';
                if (isExcluded) className += ' excluded';
                if (selectionMode === 'fixed' && !isFixed) className += ' selectable-fixed';
                if (selectionMode === 'excluded' && !isExcluded) className += ' selectable-excluded';

                return (
                    <div key={num} className={className} onClick={() => onNumberClick(num)}>
                        {num}
                    </div>
                );
            })}
        </div>
    );
};

const CheckboxGroup = ({ options, selectedOptions, onChange }) => {
    return (
        <div className="checkbox-inputs horizontal">
            {options.map(option => (
                <label key={option}>
                    <input
                        type="checkbox"
                        value={option}
                        checked={selectedOptions.includes(option)}
                        onChange={() => onChange(option)}
                    />
                    {option}
                </label>
            ))}
        </div>
    );
};

const TreeSelector = ({ includeTrees, excludeTrees, onTreeClick }) => {
    return (
        <div className="tree-selector">
            {Object.entries(TREE_MAP).map(([key, name]) => {
                const isIncluded = includeTrees.includes(key);
                const isExcluded = excludeTrees.includes(key);
                let className = 'tree-button';
                if (isIncluded) className += ' included';
                if (isExcluded) className += ' excluded';

                return (
                    <button key={key} className={className} onClick={() => onTreeClick(key)}>
                        {name}
                    </button>
                );
            })}
        </div>
    );
};

const FilterControls = ({ filters, setFilters, onGenerate, onReset, majorMeanings, loading }) => {
    const [selectionMode, setSelectionMode] = useState('fixed'); // 'fixed' or 'excluded'

    const handleNumberClick = (number) => {
        setFilters(prev => {
            const { fixedNumbers, excludedNumbers } = prev;
            let newFixed = [...fixedNumbers];
            let newExcluded = [...excludedNumbers];

            if (selectionMode === 'fixed') {
                if (newFixed.includes(number)) {
                    newFixed = newFixed.filter(n => n !== number);
                } else {
                    if (newFixed.length >= 6) {
                        alert('고정수는 최대 6개까지 선택할 수 있습니다.');
                        return prev;
                    }
                    newFixed.push(number);
                    newExcluded = newExcluded.filter(n => n !== number); // Ensure not in excluded
                }
            } else { // selectionMode === 'excluded'
                if (newExcluded.includes(number)) {
                    newExcluded = newExcluded.filter(n => n !== number);
                } else {
                    newExcluded.push(number);
                    newFixed = newFixed.filter(n => n !== number); // Ensure not in fixed
                }
            }
            return { ...prev, fixedNumbers: newFixed, excludedNumbers: newExcluded };
        });
    };

    const handleRatioChange = (filterName, value) => {
        setFilters(prev => {
            const currentValues = prev[filterName];
            const newValues = currentValues.includes(value)
                ? currentValues.filter(v => v !== value)
                : [...currentValues, value];
            return { ...prev, [filterName]: newValues };
        });
    };

    const handleTreeClick = (treeKey) => {
        setFilters(prev => {
            const { excludeTrees } = prev;
            let newExclude = [...excludeTrees];

            if (newExclude.includes(treeKey)) { // If already excluded, un-exclude it
                newExclude = newExclude.filter(t => t !== treeKey);
            } else { // If not excluded, exclude it
                if (newExclude.length >= 4) {
                    alert("모든 트리를 제외하면 조합을 생성할 수 없습니다.");
                    // Prevent adding the 5th tree
                    return prev;
                } else {
                    newExclude.push(treeKey);
                }
            }
            // The 'includeTrees' logic is completely removed.
            return { ...prev, includeTrees: [], excludeTrees: newExclude };
        });
    };

    const handleFilterChange = (e) => {
        const { name, value, type, checked } = e.target;

        setFilters(prev => {
            const newFilters = {
                ...prev,
                [name]: type === 'checkbox' ? checked : (name.includes('Range') || name.includes('Value') || name.includes('Period') || name.includes('Count')) ? parseInt(value, 10) : value
            };

            // Validation for sum range
            if (name === 'sumRangeMin' && newFilters.sumRangeMin > newFilters.sumRangeMax) {
                alert('총합수 시작 범위는 끝 범위보다 클 수 없습니다.');
                return prev;
            }
            if (name === 'sumRangeMax' && newFilters.sumRangeMax < newFilters.sumRangeMin) {
                alert('총합수 끝 범위는 시작 범위보다 작을 수 없습니다.');
                return prev;
            }

            // Validation for AC value range
            if (name === 'acValueMin' && newFilters.acValueMin > newFilters.acValueMax) {
                alert('AC값 시작 범위는 끝 범위보다 클 수 없습니다.');
                return prev;
            }
            if (name === 'acValueMax' && newFilters.acValueMax < newFilters.acValueMin) {
                alert('AC값 끝 범위는 시작 범위보다 작을 수 없습니다.');
                return prev;
            }

            // Validation for trending tree exclusion
            if (name === 'trendingTreePeriod' && newFilters.trendingTreePeriod < newFilters.trendingTreeCount) {
                alert('제외할 트리 횟수는 조회 회차보다 많을 수 없습니다.');
                return prev;
            }
            if (name === 'trendingTreeCount' && newFilters.trendingTreeCount > newFilters.trendingTreePeriod) {
                alert('제외할 트리 횟수는 조회 회차보다 많을 수 없습니다.');
                return prev;
            }

            return newFilters;
        });
    };

    const handleElementalChange = (e) => {
        const { name, value } = e.target;
        const newValue = parseInt(value, 10);

        setFilters(prev => {
            const currentBalance = prev.elementalBalance;
            const newBalance = {
                ...currentBalance,
                [name]: newValue
            };

            const total = Object.values(newBalance).reduce((sum, val) => sum + val, 0);

            if (total > 6) {
                alert('원소 균형의 총합은 6을 초과할 수 없습니다.');
                return prev; // Return previous state without changes
            }

            return {
                ...prev,
                elementalBalance: newBalance
            };
        });
    };

    const handleRecommendFilters = () => {
        setFilters(prev => ({
            ...initialFiltersState, // Start with a clean slate to reset all fields
            num_combinations: prev.num_combinations,
            oddEvenRatio: ['3:3', '4:2', '2:4'],
            highLowRatio: ['3:3', '4:2', '2:4'],
            sumRangeMin: 100,
            sumRangeMax: 170,
            acValueMin: 7,
            acValueMax: 10,
            excludeConsecutive: true,
            excludeConsecutive3: true,
            excludeMirrors: true,
            excludeSameEnding3: true,
            excludeBlacklist: true,
            excludeTrees: ['GH'], // 4번 트리 제외
            includeMajorArcana: ['5', '6', '8', '11', '20'], // 최근 6개월 미출현
            excludePastWinners: true,
            excludeSameRange: '4',
        }));
    };

    return (
        <div className="filter-grid">
            {/* 1. 고정수/제외수 */}
            <div className="filter-group span-3">
                <label data-tooltip="조합에 반드시 포함되거나 제외될 번호를 선택합니다.">고정수 및 제외수</label>
                <div className="number-selection-mode">
                    <button
                        className={`mode-button ${selectionMode === 'fixed' ? 'active' : ''}`}
                        onClick={() => setSelectionMode('fixed')}
                    >
                        고정수 ({filters.fixedNumbers.length}/6)
                    </button>
                    <button
                        className={`mode-button ${selectionMode === 'excluded' ? 'active' : ''}`}
                        onClick={() => setSelectionMode('excluded')}
                    >
                        제외수 ({filters.excludedNumbers.length})
                    </button>
                </div>
                <NumberGrid
                    filters={filters}
                    onNumberClick={handleNumberClick}
                    selectionMode={selectionMode}
                />
            </div>

            {/* 2. 홀짝 패턴 & 고저 패턴 */}
            <div className="filter-group span-3 horizontal-pair">
                <div className="filter-group">
                    <label data-tooltip="홀/짝 비율 패턴을 선택합니다. 여러 개를 선택하여 다양한 패턴의 조합을 만들 수 있습니다.">홀짝 패턴</label>
                    <CheckboxGroup
                        options={ODD_EVEN_RATIOS}
                        selectedOptions={filters.oddEvenRatio}
                        onChange={(value) => handleRatioChange('oddEvenRatio', value)}
                    />
                </div>
                <div className="filter-group">
                    <label data-tooltip="고/저 비율 패턴을 선택합니다. 여러 개를 선택하여 다양한 패턴의 조합을 만들 수 있습니다.">고저 패턴</label>
                    <CheckboxGroup
                        options={HIGH_LOW_RATIOS}
                        selectedOptions={filters.highLowRatio}
                        onChange={(value) => handleRatioChange('highLowRatio', value)}
                    />
                </div>
            </div>

            {/* 3. 총합수 & AC값 */}
            <div className="filter-group span-3 horizontal-pair">
                <div className="filter-group">
                    <label data-tooltip="6개 번호의 총합이, 설정된 범위 안에 포함되는 조합만 생성합니다. (예: 110 ~ 138)">총합수 범위</label>
                    <div className="range-inputs">
                        <select name="sumRangeMin" value={filters.sumRangeMin} onChange={handleFilterChange}>
                            {Array.from({ length: 255 - 21 + 1 }, (_, i) => 21 + i).map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                        <span>~</span>
                        <select name="sumRangeMax" value={filters.sumRangeMax} onChange={handleFilterChange}>
                            {Array.from({ length: 255 - 21 + 1 }, (_, i) => 21 + i).map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                    </div>
                </div>
                <div className="filter-group">
                    <label data-tooltip="산술적 복잡도(AC) 값의 범위를 설정합니다.&#10;- 번호들이 얼마나 골고루 분포했는지를 나타냅니다.">AC값 범위</label>
                    <div className="range-inputs">
                        <select name="acValueMin" value={filters.acValueMin} onChange={handleFilterChange}>
                            {Array.from({ length: 11 }, (_, i) => i).map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                        <span>~</span>
                        <select name="acValueMax" value={filters.acValueMax} onChange={handleFilterChange}>
                            {Array.from({ length: 11 }, (_, i) => i).map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                    </div>
                </div>
            </div>

            {/* 4. 제외 옵션 */}
            <div className="filter-group span-3">
                <label data-tooltip="- 체크박스 옵션: 연속수, 연속수(쌍), 거울수, 동끝수, 블랙리스트(등차수열, 로또용지 패턴), 과거 당첨 이력이 있는 조합을 모두 제외합니다.&#10;- 과열 트리 제외: 최근 N회차 중 특정 트리가 너무 자주 나왔다면, 해당 트리에 포함된 모든 경우의 수를 조합에서 제외시킵니다.&#10;- 동 번호대 제외: 같은 번호대역(예: 30,31,33,35) 번호가 설정한 개수 이상 포함되지 않도록 제외합니다.">제외 옵션</label>
                <div className="controls-row">
                    <div className="checkbox-inputs">
                        <label><input type="checkbox" name="excludeConsecutive" checked={filters.excludeConsecutive} onChange={handleFilterChange} /> 연속 번호 2,3쌍 제외</label>
                        <label><input type="checkbox" name="excludeConsecutive3" checked={filters.excludeConsecutive3} onChange={handleFilterChange} /> 3연번 이상 제외</label>
                        <label><input type="checkbox" name="excludeMirrors" checked={filters.excludeMirrors} onChange={handleFilterChange} /> 거울수 제외</label>
                        <label><input type="checkbox" name="excludeSameEnding3" checked={filters.excludeSameEnding3} onChange={handleFilterChange} /> 동끝수 3개 이상 제외</label>
                        <label><input type="checkbox" name="excludePastWinners" checked={filters.excludePastWinners} onChange={handleFilterChange} /> 역대 1~3등 제외</label>
                        <label><input type="checkbox" name="excludeBlacklist" checked={filters.excludeBlacklist} onChange={handleFilterChange} /> 블랙리스트(패턴) 제외</label>
                    </div>
                    <div className="range-inputs">
                        <div className="range-inputs-line">
                            <select name="trendingTreePeriod" value={filters.trendingTreePeriod} onChange={handleFilterChange}>
                                {Array.from({ length: 23 }, (_, i) => i + 4).map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                            <span>회차 중</span>
                            <select name="trendingTreeCount" value={filters.trendingTreeCount} onChange={handleFilterChange}>
                                {Array.from({ length: 13 }, (_, i) => i + 1).map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                            <span>번 이상 트리 제외</span>
                        </div>
                        <div className="range-inputs-line">
                            <select name="excludeSameRange" value={filters.excludeSameRange} onChange={handleFilterChange}>
                                <option value="0">동 번호대 제외 안함</option>
                                <option value="3">3개 이상 제외</option>
                                <option value="4">4개 이상 제외</option>
                                <option value="5">5개 이상 제외</option>
                            </select>
                            <span>동 번호대 제외</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* 5. 원소 균형 & 포함/제외 트리 */}
            <div className="filter-group span-3 horizontal-pair">
                <div className="filter-group elemental-group">
                    <label data-tooltip="조합에 포함 시킬 각 원소의 개수를 지정합니다. 1-10(컵스/물), 11-20(스워드/공기), 21-30(완드/불), 31-40(펜타클/흙), 41-45(궁정,세계/아카샤)로 대응, 다른 필터와의 상호 관계에 따라 한쪽으로 편향된 설정은 조합을 찾는데 실패할 수 있습니다.">원소 균형 (포함)</label>
                    <div className="elemental-inputs">
                        <span>💨<select name="air" value={filters.elementalBalance.air} onChange={handleElementalChange}>{Array.from({ length: 7 }, (_, i) => i).map(n => <option key={n} value={n}>{n}</option>)}</select></span>
                        <span>💧<select name="water" value={filters.elementalBalance.water} onChange={handleElementalChange}>{Array.from({ length: 7 }, (_, i) => i).map(n => <option key={n} value={n}>{n}</option>)}</select></span>
                        <span>🔥<select name="fire" value={filters.elementalBalance.fire} onChange={handleElementalChange}>{Array.from({ length: 7 }, (_, i) => i).map(n => <option key={n} value={n}>{n}</option>)}</select></span>
                        <span>🌿<select name="earth" value={filters.elementalBalance.earth} onChange={handleElementalChange}>{Array.from({ length: 7 }, (_, i) => i).map(n => <option key={n} value={n}>{n}</option>)}</select></span>
                        <span>✨<select name="spirit" value={filters.elementalBalance.spirit} onChange={handleElementalChange}>{Array.from({ length: 6 }, (_, i) => i).map(n => <option key={n} value={n}>{n}</option>)}</select></span>
                    </div>
                </div>
                <div className="filter-group tree-group">
                    <label data-tooltip="통계적으로 당첨 확률이 낮다고 생각되는 트리에 속한 모든 조합이 제외됩니다.">제외할 트리</label>
                    <TreeSelector
                        includeTrees={filters.includeTrees}
                        excludeTrees={filters.excludeTrees}
                        onTreeClick={handleTreeClick}
                    />
                </div>
            </div>

            {/* 6. 메이저 아르카나 */}
            <div className="filter-group span-3">
                <label data-tooltip="선택한 메이저 타로카드에 대응되는 값을 갖는 조합으로만 생성됩니다. 필터 설정에 따라 해당 하는 조합을 찾지 못할 수 있습니다.">메이저 아르카나 (포함)</label>
                <div className="arcana-grid">
                    {majorMeanings && Object.keys(majorMeanings).map(key => (
                        <div key={key} className="arcana-item">
                            <input type="checkbox" id={`arcana-${key}`} name="includeMajorArcana" value={key} checked={filters.includeMajorArcana.includes(key)} onChange={(e) => handleRatioChange('includeMajorArcana', e.target.value)} />
                            <label htmlFor={`arcana-${key}`}>{majorMeanings[key].korean_name}</label>
                        </div>
                    ))}
                </div>
            </div>

            {/* 7. 생성 버튼 영역 */}
            <div className="filter-actions-content">
                <div className="combination-count-group">
                    <label>조합 개수</label>
                    <select name="num_combinations" value={filters.num_combinations} onChange={handleFilterChange}>
                        <option value="1">1개</option>
                        <option value="2">2개</option>
                        <option value="3">3개</option>
                        <option value="4">4개</option>
                        <option value="5">5개</option>
                    </select>
                </div>
                <div className="action-buttons-group">
                    <button className="action-button secondary" onClick={onReset}>초기화</button>
                    <button className="action-button recommend-button" onClick={handleRecommendFilters}>추천 필터 적용</button>
                    <button className="action-button generate-filtered-button" onClick={onGenerate} disabled={loading}>조합 생성</button>
                </div>
            </div>
        </div>
    );
};

const Drawdown = ({ options, onSelect }) => {
    return (
        <div className="drawdown-list">
            {options.map(option => (
                <div key={option} className="drawdown-item" onClick={() => onSelect(option)}>
                    {option}회
                </div>
            ))}
        </div>
    );
};


const AppFooter = ({ setCurrentPage }) => {
    const handleLinkClick = (e, page) => {
        e.preventDefault();
        setCurrentPage(page);
    };

    return (
        <footer className="app-footer">
            <p className="footer-disclaimer">
                이 사이트는 쿠팡 파트너스 활동의 일환으로, 이에 따른 일정액의 수수료를 제공받습니다.
            </p>
            <div className="footer-links">
                <a href="#!" onClick={(e) => handleLinkClick(e, 'terms')}>서비스 이용약관</a> |
                <a href="#!" onClick={(e) => handleLinkClick(e, 'privacy')}>개인정보처리방침</a> | 
                <a href="mailto:contact.fraction885@simplelogin.com">문의하기</a> {/*광고/제휴 문의, 공지사항 추가 가능*/}
            </div>
            <p className="footer-disclaimer">
                LOTTO & TAROT의 분석 시스템은 누적된 통계 데이터와 타로의 수비학적 관점을 융합/필터링한 정보 제공만을 목적으로 하며, 당첨을 보장하거나 확률을 개선하는 서비스가 아니므로 서비스 이용 과정에서 기대이익을 얻지 못하거나 발생한 손해 등에 대한 최종 책임은 서비스 이용자 본인에게 있습니다.
            </p>
            <p className="footer-copyright">
                © 2025 LOTTO & TAROT. ALL RIGHTS RESERVED.
            </p>
        </footer>
    );
};

export default App;
